import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  DollarSign, Plus, Search, Settings, FileText, Users, 
  CreditCard, AlertTriangle, CheckCircle, Printer, Receipt,
  TrendingUp, Clock, Ban, Bell, Send, Zap, Download, Eye,
  Copy, RefreshCw, BookOpen, Tag, Calendar, Trash2, Pencil, Loader2
} from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FEE_TYPES = [
  { value: 'admission', label: 'Admission Fee' },
  { value: 're_admission', label: 'Re-Admission Fee' },
  { value: 'registration', label: 'Registration Fee' },
  { value: 'tuition', label: 'Tuition Fee' },
  { value: 'transport', label: 'Transport Fee' },
  { value: 'hostel', label: 'Hostel Fee' },
  { value: 'exam', label: 'Examination Fee' },
  { value: 'library', label: 'Library Fee' },
  { value: 'sports', label: 'Sports Fee' },
  { value: 'lab', label: 'Lab Fee' },
  { value: 'development', label: 'Development Fee' },
  { value: 'annual', label: 'Annual Fee' },
  { value: 'late_fine', label: 'Late Fine' },
  { value: 'other', label: 'Other' },
];

const FEE_CATEGORIES = [
  { value: 'one_time', label: 'One-Time (Admission, Registration)', isRecurring: false, frequency: 'one_time' },
  { value: 'annual', label: 'Annual (Once per year)', isRecurring: false, frequency: 'yearly' },
  { value: 'monthly', label: 'Monthly (Every month)', isRecurring: true, frequency: 'monthly' },
  { value: 'quarterly', label: 'Quarterly (Every 3 months)', isRecurring: true, frequency: 'quarterly' },
];

const PAYMENT_MODES = [
  { value: 'cash', label: 'Cash' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'dd', label: 'Demand Draft' },
  { value: 'bank_transfer', label: 'Bank Transfer/NEFT' },
  { value: 'upi', label: 'UPI' },
  { value: 'card', label: 'Card/POS' },
  { value: 'online', label: 'Online (Razorpay)' },
];

// Student Fee View Component (for students and parents)
const StudentFeeView = ({ getAuthHeader, user }) => {
  const [feeData, setFeeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [receiptData, setReceiptData] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);

  useEffect(() => {
    if (user?.role === 'parent') {
      fetchChildren();
    } else {
      fetchMyFees();
    }
  }, [user]);

  useEffect(() => {
    if (selectedChild) {
      fetchChildFees(selectedChild.id);
    }
  }, [selectedChild]);

  const fetchChildren = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/students/parent/children`, { headers: getAuthHeader() });
      const childrenData = res.data || [];
      setChildren(childrenData);
      if (childrenData.length > 0) {
        setSelectedChild(childrenData[0]);
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Failed to fetch children:', error);
      setLoading(false);
    }
  };

  const fetchChildFees = async (studentId) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/fee-management/student-fees/${studentId}`, { headers: getAuthHeader() });
      setFeeData(res.data);
    } catch (error) {
      console.error('Failed to fetch fees:', error);
      // Set empty data instead of showing error for no invoices
      setFeeData({ invoices: [], summary: { total_fees: 0, total_paid: 0, total_due: 0, pending_invoices: 0 } });
    } finally {
      setLoading(false);
    }
  };

  const fetchMyFees = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/fee-management/my-fees`, { headers: getAuthHeader() });
      setFeeData(res.data);
    } catch (error) {
      console.error('Failed to fetch fees:', error);
      toast.error('Failed to load fee details');
    } finally {
      setLoading(false);
    }
  };

  const viewReceipt = async (invoiceId) => {
    try {
      const res = await axios.get(`${API_URL}/api/fee-management/receipt/${invoiceId}`, { headers: getAuthHeader() });
      setReceiptData(res.data);
      setShowReceipt(true);
    } catch (error) {
      toast.error('Failed to load receipt');
    }
  };

  const printReceipt = () => {
    const printContent = document.getElementById('receipt-content');
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Fee Receipt</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .header { text-align: center; margin-bottom: 20px; }
            .header h2 { margin: 0; }
            .receipt-body { margin: 20px 0; }
            table { width: 100%; border-collapse: collapse; margin: 10px 0; }
            th, td { padding: 8px; text-align: left; border-bottom: 1px solid #ddd; }
            .amount { font-size: 24px; font-weight: bold; color: green; }
            .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; }
          </style>
        </head>
        <body>${printContent.innerHTML}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  if (loading) {
    return (
      <DashboardLayout title="My Fees">
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </DashboardLayout>
    );
  }

  const summary = feeData?.summary || {};
  const invoices = feeData?.invoices || [];
  const pendingInvoices = invoices.filter(i => ['pending', 'partial', 'overdue'].includes(i.status));
  const paidInvoices = invoices.filter(i => i.status === 'paid');

  return (
    <DashboardLayout title="My Fees">
      <div className="space-y-6">
        {/* Child Selector for Parents */}
        {user?.role === 'parent' && children.length > 1 && (
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <Label className="text-sm font-medium">Select Child:</Label>
                <div className="flex gap-2">
                  {children.map((child) => (
                    <Button
                      key={child.id}
                      variant={selectedChild?.id === child.id ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSelectedChild(child)}
                    >
                      {child.name}
                      <span className="ml-1 text-xs opacity-70">({child.class_name} {child.section || ''})</span>
                    </Button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Selected Child Info */}
        {selectedChild && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Users className="h-4 w-4" />
            Showing fees for: <span className="font-medium text-foreground">{selectedChild.name}</span>
            <span className="text-xs">({selectedChild.class_name} {selectedChild.section || ''} • Roll: {selectedChild.roll_number})</span>
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <DollarSign className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Fees</p>
                  <p className="text-xl font-bold">₹{(summary.total_fees || 0).toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Paid</p>
                  <p className="text-xl font-bold text-green-600">₹{(summary.total_paid || 0).toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Due</p>
                  <p className="text-xl font-bold text-red-600">₹{(summary.total_due || 0).toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Clock className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Pending Invoices</p>
                  <p className="text-xl font-bold">{summary.pending_invoices || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pending Fees */}
        {pendingInvoices.length > 0 && (
          <Card className="border-red-200">
            <CardHeader className="bg-red-50">
              <CardTitle className="text-red-700 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Pending Fees ({pendingInvoices.length})
              </CardTitle>
              <CardDescription>Please pay these fees to avoid late charges</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y">
                {pendingInvoices.map(inv => (
                  <div key={inv.id} className="p-4 flex justify-between items-center hover:bg-muted/30">
                    <div>
                      <p className="font-medium">
                        {inv.fee_head_name || (inv.items?.length > 0 
                          ? `Combined: ${inv.items.map(i => i.fee_name).join(', ')}`
                          : inv.invoice_type === 'promotion' ? 'Promotion Fees' 
                          : inv.invoice_type === 'hostel' ? 'Hostel Fee'
                          : inv.invoice_type === 'transport' ? 'Transport Fee'
                          : 'Fee')}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Due: {inv.due_date} | Invoice: {inv.invoice_number}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg">₹{(inv.total_amount || inv.net_amount || inv.amount || 0).toLocaleString()}</p>
                      <Badge variant={inv.status === 'overdue' ? 'destructive' : 'secondary'}>
                        {inv.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Payment History */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              Payment History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {paidInvoices.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No payment history</p>
            ) : (
              <div className="space-y-3">
                {paidInvoices.map(inv => (
                  <div key={inv.id} className="p-3 border rounded-lg flex justify-between items-center">
                    <div>
                      <p className="font-medium">{inv.fee_head_name || 'Fee'}</p>
                      <p className="text-sm text-muted-foreground">
                        {inv.receipt_number && <span className="text-primary font-medium">{inv.receipt_number} | </span>}
                        Paid on: {inv.payment_date || inv.last_payment_date || inv.due_date}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="font-bold text-green-600">₹{(inv.paid_amount || inv.net_amount || inv.amount || 0).toLocaleString()}</p>
                        <Badge className="bg-green-100 text-green-700">Paid</Badge>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => viewReceipt(inv.id)}
                        data-testid={`view-receipt-${inv.id}`}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Receipt
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Receipt Dialog */}
        <Dialog open={showReceipt} onOpenChange={setShowReceipt}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Fee Receipt</DialogTitle>
            </DialogHeader>
            {receiptData && (
              <div id="receipt-content" className="space-y-4">
                {/* School Header */}
                <div className="text-center border-b pb-4">
                  {receiptData.school?.logo_url && (
                    <img src={receiptData.school.logo_url} alt="School Logo" className="h-16 mx-auto mb-2" />
                  )}
                  <h2 className="text-xl font-bold">{receiptData.school?.name || 'School'}</h2>
                  <p className="text-sm text-muted-foreground">{receiptData.school?.address}</p>
                  <p className="text-sm text-muted-foreground">Phone: {receiptData.school?.phone} | Email: {receiptData.school?.email}</p>
                </div>

                {/* Receipt Title */}
                <div className="text-center bg-primary/10 py-2">
                  <h3 className="text-lg font-semibold">FEE RECEIPT</h3>
                  <p className="text-sm font-medium">Receipt No: {receiptData.payment?.receipt_number || receiptData.invoice?.receipt_number || 'N/A'}</p>
                </div>

                {/* Student Details */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
                  <div>
                    <p className="text-sm text-muted-foreground">Student Name</p>
                    <p className="font-medium">{receiptData.student?.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Class</p>
                    <p className="font-medium">{receiptData.student?.class_name} {receiptData.student?.section}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Roll No</p>
                    <p className="font-medium">{receiptData.student?.student_id}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Father's Name</p>
                    <p className="font-medium">{receiptData.student?.father_name || '-'}</p>
                  </div>
                </div>

                {/* Fee Details */}
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2">Description</th>
                      <th className="text-right py-2">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="py-2">{receiptData.invoice?.fee_head_name || 'Fee'}</td>
                      <td className="text-right py-2">₹{(receiptData.invoice?.net_amount || receiptData.invoice?.paid_amount || 0).toLocaleString()}</td>
                    </tr>
                    {receiptData.invoice?.discount_amount > 0 && (
                      <tr>
                        <td className="py-2 text-green-600">Discount</td>
                        <td className="text-right py-2 text-green-600">-₹{receiptData.invoice.discount_amount.toLocaleString()}</td>
                      </tr>
                    )}
                    {receiptData.invoice?.late_fine > 0 && (
                      <tr>
                        <td className="py-2 text-red-600">Late Fine</td>
                        <td className="text-right py-2 text-red-600">+₹{receiptData.invoice.late_fine.toLocaleString()}</td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot>
                    <tr className="border-t font-bold">
                      <td className="py-2">Total Paid</td>
                      <td className="text-right py-2 text-green-600">₹{(receiptData.invoice?.paid_amount || 0).toLocaleString()}</td>
                    </tr>
                  </tfoot>
                </table>

                {/* Payment Details */}
                {receiptData.transactions?.length > 0 && (
                  <div className="p-4 bg-green-50 rounded-lg">
                    <p className="text-sm font-medium mb-2">Payment Details:</p>
                    {receiptData.transactions.map((txn, idx) => (
                      <div key={idx} className="text-sm flex justify-between">
                        <span>{txn.payment_mode} - {txn.payment_date}</span>
                        <span>₹{txn.amount?.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Footer */}
                <div className="text-center text-xs text-muted-foreground border-t pt-4">
                  <p>This is a computer-generated receipt and does not require a signature.</p>
                  <p>Generated on: {new Date().toLocaleDateString()}</p>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowReceipt(false)}>Close</Button>
              <Button onClick={printReceipt}>
                <Printer className="h-4 w-4 mr-2" />
                Print Receipt
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Info Note */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <p className="text-sm text-blue-700">
              <strong>Note:</strong> For fee payments, please visit the school office or use the online payment option if available. 
              Contact the accounts department for any fee-related queries.
            </p>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

// Admin Fee Management Component
const AdminFeeManagement = () => {
  const { getAuthHeader, user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('collection');
  const [loading, setLoading] = useState(true);
  
  // Data
  const [feeHeads, setFeeHeads] = useState([]);
  const [feeStructures, setFeeStructures] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [studentFeeCategories, setStudentFeeCategories] = useState([]);
  const [classes, setClasses] = useState([]);
  const [dailyReport, setDailyReport] = useState(null);
  const [defaulters, setDefaulters] = useState(null);
  const [defaultersPage, setDefaultersPage] = useState(1);
  const [defaultersPageSize] = useState(50);
  const [headWiseReport, setHeadWiseReport] = useState(null);
  const [monthSummary, setMonthSummary] = useState(null);
  const [classWiseReport, setClassWiseReport] = useState(null);
  const [discountReport, setDiscountReport] = useState(null);
  const [fineWaiverReport, setFineWaiverReport] = useState(null);
  
  // Report filters
  const [reportFromDate, setReportFromDate] = useState(new Date().toISOString().slice(0, 8) + '01');
  const [reportToDate, setReportToDate] = useState(new Date().toISOString().slice(0, 10));
  const [reportFeeHead, setReportFeeHead] = useState('');
  const [reportType, setReportType] = useState('all');
  // Derived month for backward compatibility with fetchReports
  const reportMonth = reportFromDate ? reportFromDate.slice(0, 7) : new Date().toISOString().slice(0, 7);
  
  // Collection
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [pendingInvoices, setPendingInvoices] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [collectionDialog, setCollectionDialog] = useState(false);
  const [receiptDialog, setReceiptDialog] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [generateInvoiceDialog, setGenerateInvoiceDialog] = useState(false);
  const [generateMonth, setGenerateMonth] = useState(new Date().toISOString().slice(0, 7));
  const [generating, setGenerating] = useState(false);
  
  // Payment History
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [paymentHistoryLoading, setPaymentHistoryLoading] = useState(false);
  const [historyDateFrom, setHistoryDateFrom] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().slice(0, 10);
  });
  const [historyDateTo, setHistoryDateTo] = useState(new Date().toISOString().slice(0, 10));
  const [historySearchQuery, setHistorySearchQuery] = useState('');
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  
  // Delete Payment
  const [deletePaymentDialog, setDeletePaymentDialog] = useState(false);
  const [deletePaymentPreview, setDeletePaymentPreview] = useState(null);
  const [deletingPayment, setDeletingPayment] = useState(false);
  
  // Reminder settings
  const [reminderSettings, setReminderSettings] = useState({
    enabled: false,
    days_before_due: 7,
    send_on_due_date: true,
    days_after_due: [1, 3, 7],
    channels: ['in_app']
  });
  const [sendingReminders, setSendingReminders] = useState(false);
  
  // Forms
  const [paymentForm, setPaymentForm] = useState({
    payment_mode: 'cash',
    amount_paid: 0,
    cheque_number: '',
    cheque_date: '',
    bank_name: '',
    transaction_id: '',
    upi_id: '',
    remarks: '',
    waive_fine: false,  // Full fine waive option
    fine_discount: 0,   // Partial fine discount amount
    applied_discount_id: '',  // Selected discount/concession
    applied_discount_amount: 0  // Calculated discount amount
  });
  const [isCollectingPayment, setIsCollectingPayment] = useState(false);  // Prevent double-click
  
  // Setup dialogs
  const [feeHeadDialog, setFeeHeadDialog] = useState(false);
  const [structureDialog, setStructureDialog] = useState(false);
  const [discountDialog, setDiscountDialog] = useState(false);
  const [feeCategoryDialog, setFeeCategoryDialog] = useState(false);
  
  const [feeHeadForm, setFeeHeadForm] = useState({
    name: '', code: '', fee_type: 'tuition', fee_category: 'one_time', is_recurring: false, frequency: 'one_time', 
    apply_on_new_admission: true, apply_on_re_admission: true, apply_on_promotion: true, apply_on_transfer: true, description: ''
  });
  const [editingFeeHead, setEditingFeeHead] = useState(null);  // For edit mode
  
  const [discountForm, setDiscountForm] = useState({
    name: '', code: '', discount_type: 'percentage', discount_value: '', applicable_fee_heads: [], description: ''
  });
  const [editingDiscount, setEditingDiscount] = useState(null);  // For edit mode
  
  const [feeCategoryForm, setFeeCategoryForm] = useState({
    name: '', code: '', discount_percentage: 0, description: ''
  });
  const [editingCategory, setEditingCategory] = useState(null);  // For edit mode
  
  // Templates
  const [feeTemplates, setFeeTemplates] = useState([]);
  const [templateDialog, setTemplateDialog] = useState(false);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  
  // Admission Receipt
  const [admissionReceiptDialog, setAdmissionReceiptDialog] = useState(false);
  const [admissionReceiptData, setAdmissionReceiptData] = useState(null);
  const [receiptStudentSearch, setReceiptStudentSearch] = useState('');
  const [receiptSearchResults, setReceiptSearchResults] = useState([]);
  
  // Due Fees Dashboard
  const [dueFeesSummary, setDueFeesSummary] = useState(null);
  const [loadingDues, setLoadingDues] = useState(false);
  
  // Full Year & Bulk Payment
  const [fullYearDialog, setFullYearDialog] = useState(false);
  const [fullYearStudent, setFullYearStudent] = useState(null);
  const [fullYearData, setFullYearData] = useState(null);
  const [generatingFullYear, setGeneratingFullYear] = useState(false);
  const [bulkPaymentDialog, setBulkPaymentDialog] = useState(false);
  const [studentFeeDetails, setStudentFeeDetails] = useState(null);
  
  // Confirmation dialogs for manual invoice/fine generation
  const [invoiceConfirmDialog, setInvoiceConfirmDialog] = useState(false);
  const [fineConfirmDialog, setFineConfirmDialog] = useState(false);
  const [invoiceCheckResult, setInvoiceCheckResult] = useState(null);
  const [fineCheckResult, setFineCheckResult] = useState(null);
  const [applyingFines, setApplyingFines] = useState(false);
  
  // Bulk Assign One-Time Fee
  const [bulkAssignDialog, setBulkAssignDialog] = useState(false);
  const [bulkAssignForm, setBulkAssignForm] = useState({
    class_id: '', fee_head_id: '', amount: ''
  });
  const [bulkAssignStudents, setBulkAssignStudents] = useState([]);
  const [bulkAssigning, setBulkAssigning] = useState(false);
  
  // Quick Fee - simplified one-click fee assignment
  const [quickFeeDialog, setQuickFeeDialog] = useState(false);
  const [quickFeeForm, setQuickFeeForm] = useState({
    name: '', amount: '', class_id: '', due_date: ''
  });
  const [quickFeeStudents, setQuickFeeStudents] = useState([]);
  const [quickFeeAssigning, setQuickFeeAssigning] = useState(false);
  
  // Advance Invoice Generation (Transport & Hostel only - Tuition is auto-generated for full year)
  const [advanceMonths, setAdvanceMonths] = useState(3);
  const [advanceFeeTypes, setAdvanceFeeTypes] = useState({
    transport: true,
    hostel: true
  }); // Multiple selection with checkboxes
  const [generatingAdvance, setGeneratingAdvance] = useState(false);
  const [advanceResult, setAdvanceResult] = useState(null);
  const [deletingWrongInvoices, setDeletingWrongInvoices] = useState(false);
  
  // Current academic session
  const [currentSession, setCurrentSession] = useState(null);
  const [academicSessions, setAcademicSessions] = useState([]);  // All academic sessions for dropdown
  
  const [structureForm, setStructureForm] = useState({
    academic_year: '', class_id: '', fee_head_id: '', amount: '', due_day: 10, 
    late_fine_type: 'per_day', late_fine_per_day: 0, late_fine_monthly: 0, max_late_fine: 0
  });
  const [editingStructure, setEditingStructure] = useState(null);  // For edit mode

  useEffect(() => {
    fetchCurrentSession();
  }, []);

  useEffect(() => {
    if (currentSession) {
      fetchData();
    }
  }, [activeTab, currentSession]);

  const fetchCurrentSession = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/academic/sessions`, { headers: getAuthHeader() });
      // Find the current session
      const sessions = res.data || [];
      setAcademicSessions(sessions);  // Store all sessions for dropdown
      const current = sessions.find(s => s.is_current);
      if (current) {
        setCurrentSession(current);
        setStructureForm(prev => ({ ...prev, academic_year: current.session_name || '' }));
      } else if (sessions.length > 0) {
        // Use the first session if none is marked current
        setCurrentSession(sessions[0]);
        setStructureForm(prev => ({ ...prev, academic_year: sessions[0].session_name || '' }));
      }
    } catch (error) {
      console.warn('No active session found');
      // Use default if no session
      const currentYear = new Date().getFullYear();
      const defaultSession = `${currentYear}-${currentYear + 1}`;
      setCurrentSession({ session_name: defaultSession });
      setStructureForm(prev => ({ ...prev, academic_year: defaultSession }));
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [headsRes, structuresRes, discountsRes, classesRes, categoriesRes] = await Promise.all([
        axios.get(`${API_URL}/api/fee-management/fee-heads`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/fee-management/fee-structures`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/fee-management/discounts`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/classes`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/fee-management/student-categories`, { headers: getAuthHeader() })
      ]);
      setFeeHeads(headsRes.data || []);
      setFeeStructures(structuresRes.data || []);
      setDiscounts(discountsRes.data || []);
      setStudentFeeCategories(categoriesRes.data || []);
      // Handle paginated classes response
      setClasses(classesRes.data?.items || classesRes.data || []);
      
      // Fetch reports
      try {
        const dailyRes = await axios.get(`${API_URL}/api/fee-management/reports/daily-collection`, { headers: getAuthHeader() });
        setDailyReport(dailyRes.data);
        // Fetch defaulters separately with pagination
        fetchDefaulters(1);
        // Fetch due fees summary for consistent data across all tabs
        fetchDueFeesSummary();
      } catch (reportError) {
        console.warn('Reports not available');
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load fee management data');
    } finally {
      setLoading(false);
    }
  };

  const fetchReports = async (month) => {
    try {
      const academicYear = currentSession?.session_name || `${new Date().getFullYear()}-${new Date().getFullYear() + 1}`;
      const [headWiseRes, monthSummaryRes, classWiseRes, discountRes, fineWaiverRes] = await Promise.all([
        axios.get(`${API_URL}/api/fee-management/reports/head-wise?academic_year=${academicYear}&month=${month}`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/fee-management/reports/month-summary?month=${month}`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/fee-management/reports/class-wise?academic_year=${academicYear}&month=${month}`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/fee-management/reports/discount-summary?month=${month}`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/fee-management/reports/fine-waiver?month=${month}`, { headers: getAuthHeader() })
      ]);
      setHeadWiseReport(headWiseRes.data);
      setMonthSummary(monthSummaryRes.data);
      setClassWiseReport(classWiseRes.data);
      setDiscountReport(discountRes.data);
      setFineWaiverReport(fineWaiverRes.data);
    } catch (error) {
      console.error('Failed to fetch reports');
    }
  };

  // Fetch Defaulters with pagination
  const fetchDefaulters = async (page = 1) => {
    try {
      const res = await axios.get(
        `${API_URL}/api/fee-management/reports/defaulters?page=${page}&page_size=${defaultersPageSize}`,
        { headers: getAuthHeader() }
      );
      setDefaulters(res.data);
      setDefaultersPage(page);
    } catch (error) {
      console.error('Failed to fetch defaulters');
    }
  };

  // Fetch Payment History with date range
  const fetchPaymentHistory = async (page = 1) => {
    setPaymentHistoryLoading(true);
    try {
      const params = new URLSearchParams({
        page: page,
        page_size: 20,
        date_from: historyDateFrom,
        date_to: historyDateTo
      });
      if (historySearchQuery) params.append('search', historySearchQuery);
      
      const res = await axios.get(`${API_URL}/api/fee-management/payments?${params}`, { headers: getAuthHeader() });
      setPaymentHistory(res.data.items || []);
      setHistoryPage(res.data.page || 1);
      setHistoryTotalPages(res.data.total_pages || 1);
    } catch (error) {
      console.error('Failed to fetch payment history');
      toast.error('Failed to load payment history');
    } finally {
      setPaymentHistoryLoading(false);
    }
  };

  // Preview delete payment
  const previewDeletePayment = async (payment) => {
    setDeletingPayment(true);
    try {
      const res = await axios.delete(
        `${API_URL}/api/fee-management/admin/delete-payment/${payment.id}?confirm=false`,
        { headers: getAuthHeader() }
      );
      setDeletePaymentPreview(res.data);
      setDeletePaymentDialog(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to preview payment deletion');
    } finally {
      setDeletingPayment(false);
    }
  };

  // Confirm delete payment
  const confirmDeletePayment = async () => {
    if (!deletePaymentPreview?.payment?.id) return;
    
    setDeletingPayment(true);
    try {
      const res = await axios.delete(
        `${API_URL}/api/fee-management/admin/delete-payment/${deletePaymentPreview.payment.id}?confirm=true`,
        { headers: getAuthHeader() }
      );
      toast.success(res.data.message || 'Payment deleted successfully');
      setDeletePaymentDialog(false);
      setDeletePaymentPreview(null);
      // Refresh payment history
      fetchPaymentHistory(historyPage);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete payment');
    } finally {
      setDeletingPayment(false);
    }
  };

  // Download receipt PDF
  const downloadReceiptPDF = async (paymentId, receiptNumber) => {
    try {
      toast.loading('Downloading receipt...');
      const response = await axios.get(
        `${API_URL}/api/fee-management/payment-receipt/${paymentId}/pdf`,
        {
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      // Create blob URL and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Receipt_${receiptNumber || paymentId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.dismiss();
      toast.success('Receipt downloaded!');
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to download receipt');
      console.error('Download error:', error);
    }
  };

  // Check if invoices already exist for the month before generating
  const checkAndGenerateInvoices = async () => {
    try {
      // Check existing invoices for the month
      const res = await axios.get(
        `${API_URL}/api/fee-management/check-monthly-invoices?target_month=${generateMonth}`,
        { headers: getAuthHeader() }
      );
      setInvoiceCheckResult(res.data);
      setInvoiceConfirmDialog(true);
    } catch (error) {
      // If check fails, show confirmation anyway
      setInvoiceCheckResult({ exists: false, count: 0 });
      setInvoiceConfirmDialog(true);
    }
  };

  const generateMonthlyInvoices = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/fee-management/generate-monthly-invoices?target_month=${generateMonth}`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(res.data.message);
      setGenerateInvoiceDialog(false);
      setInvoiceConfirmDialog(false);
      fetchDueFeesSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate invoices');
    } finally {
      setGenerating(false);
    }
  };

  // Check and apply late fines
  const checkAndApplyFines = async () => {
    try {
      const res = await axios.get(
        `${API_URL}/api/fee-management/check-late-fines`,
        { headers: getAuthHeader() }
      );
      setFineCheckResult(res.data);
      setFineConfirmDialog(true);
    } catch (error) {
      setFineCheckResult({ last_run: null, overdue_count: 0 });
      setFineConfirmDialog(true);
    }
  };

  const applyLateFines = async () => {
    setApplyingFines(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/fee-management/update-late-fines`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(res.data.message || 'Late fines applied successfully');
      setFineConfirmDialog(false);
      fetchReports(reportMonth);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply late fines');
    } finally {
      setApplyingFines(false);
    }
  };

  // Run All Missed Cron Jobs
  const runMissedCronJobs = async () => {
    try {
      toast.info('Running all cron jobs... Please wait');
      const res = await axios.post(
        `${API_URL}/api/fee-management/run-missed-cron-jobs`,
        {},
        { headers: getAuthHeader() }
      );
      
      // Show results
      const results = res.data.results || {};
      let successCount = 0;
      let errorCount = 0;
      
      Object.entries(results).forEach(([job, status]) => {
        if (status && status.includes('✅')) {
          successCount++;
        } else if (status && status.includes('❌')) {
          errorCount++;
        }
      });
      
      if (errorCount === 0) {
        toast.success(`All ${successCount} cron jobs completed successfully!`);
      } else {
        toast.warning(`${successCount} jobs succeeded, ${errorCount} jobs failed. Check console for details.`);
        console.log('Cron job results:', results);
      }
      
      // Refresh data
      fetchReports(reportMonth);
      fetchDueFeesSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to run cron jobs');
    }
  };

  // Export Reports to Excel
  const exportReportsToExcel = async (type = 'all') => {
    try {
      const exportType = type || reportType || 'all';
      toast.info('Generating Excel report...');
      let url = `${API_URL}/api/fee-management/reports/export-excel?report_type=${exportType}&from_date=${reportFromDate}&to_date=${reportToDate}`;
      if (reportFeeHead) url += `&fee_head=${encodeURIComponent(reportFeeHead)}`;
      const response = await axios.get(url, { 
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      // Create download link
      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = downloadUrl;
      const feeLabel = reportFeeHead ? `_${reportFeeHead.replace(/\s/g, '_')}` : '';
      link.setAttribute('download', `fee_report_${exportType}${feeLabel}_${reportFromDate}_to_${reportToDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('Excel report downloaded successfully!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error(error.response?.data?.detail || 'Failed to export report');
    }
  };

  // Fix all due dates and apply late fines
  const fixDueDatesAndFines = async () => {
    if (!window.confirm('This will fix all invoice due dates and apply late fines. Continue?')) {
      return;
    }
    try {
      toast.info('Fixing due dates and applying late fines...');
      const res = await axios.post(
        `${API_URL}/api/fee-management/fix-all-due-dates-and-fines?dry_run=false`,
        {},
        { headers: getAuthHeader() }
      );
      
      const { due_date_fixed, late_fine_applied, total_invoices } = res.data;
      toast.success(`Fixed ${due_date_fixed} due dates and applied ${late_fine_applied} late fines out of ${total_invoices} invoices`);
      
      // Refresh data
      fetchReports(reportMonth);
      fetchDueFeesSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fix due dates');
    }
  };

  // Cleanup wrong admission invoices
  const cleanupWrongAdmissionInvoices = async () => {
    // First do a dry run to show what will be deleted
    try {
      toast.info('Scanning for wrong invoices...');
      const dryRunRes = await axios.post(
        `${API_URL}/api/fee-management/cleanup-wrong-admission-invoices?dry_run=true`,
        {},
        { headers: getAuthHeader() }
      );
      
      const { wrong_invoices_found, details } = dryRunRes.data;
      
      if (wrong_invoices_found === 0) {
        toast.success('No wrong invoices found! Everything is correct.');
        return;
      }
      
      // Show confirmation with details
      const detailText = details.slice(0, 10).map(d => 
        `${d.student_name}: ${d.fee_name} (${d.reason})`
      ).join('\n');
      
      const confirmMsg = `Found ${wrong_invoices_found} wrong invoices:\n\n${detailText}${wrong_invoices_found > 10 ? '\n...and more' : ''}\n\nDelete these invoices?`;
      
      if (!window.confirm(confirmMsg)) {
        return;
      }
      
      // Actually delete
      const res = await axios.post(
        `${API_URL}/api/fee-management/cleanup-wrong-admission-invoices?dry_run=false`,
        {},
        { headers: getAuthHeader() }
      );
      
      toast.success(`Deleted ${res.data.deleted} wrong invoices`);
      
      // Refresh data
      fetchReports(reportMonth);
      fetchDueFeesSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cleanup invoices');
    }
  };

  // Fix Academic Year in Fee Structures and Invoices
  const fixAcademicYear = async () => {
    const fromYear = window.prompt('Enter OLD academic year to fix (e.g., 2025-2026):', '2025-2026');
    if (!fromYear) return;
    
    const toYear = window.prompt('Enter NEW academic year (e.g., 2026-2027):', '2026-2027');
    if (!toYear) return;
    
    try {
      // First preview
      toast.info('Checking what needs to be fixed...');
      const previewRes = await axios.get(
        `${API_URL}/api/fee-management/fix-academic-year/preview?from_year=${encodeURIComponent(fromYear)}&to_year=${encodeURIComponent(toYear)}`,
        { headers: getAuthHeader() }
      );
      
      const { fee_structures_to_update, pending_invoices_to_update, all_invoices_with_old_year } = previewRes.data;
      
      if (fee_structures_to_update === 0 && pending_invoices_to_update === 0) {
        toast.success(`No records found with academic year ${fromYear}. Nothing to fix!`);
        return;
      }
      
      const confirmMsg = `Found:\n• ${fee_structures_to_update} Fee Structures\n• ${pending_invoices_to_update} Pending Invoices\n• ${all_invoices_with_old_year} Total Invoices\n\nwith academic year "${fromYear}".\n\nChange all to "${toYear}"?`;
      
      if (!window.confirm(confirmMsg)) {
        return;
      }
      
      // Actually fix
      toast.info('Fixing academic year...');
      const res = await axios.post(
        `${API_URL}/api/fee-management/fix-academic-year?from_year=${encodeURIComponent(fromYear)}&to_year=${encodeURIComponent(toYear)}&fix_fee_structures=true&fix_invoices=true&fix_pending_only=false`,
        {},
        { headers: getAuthHeader() }
      );
      
      if (res.data.success) {
        toast.success(`Fixed! ${res.data.fee_structures_updated} fee structures and ${res.data.invoices_updated} invoices updated from ${fromYear} to ${toYear}`);
        fetchReports(reportMonth);
        fetchDueFeesSummary();
      } else {
        toast.error('Failed to fix: ' + (res.data.errors?.join(', ') || 'Unknown error'));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fix academic year');
    }
  };

  // Bulk Change Academic Year for Fee Structures only
  const bulkChangeStructureSession = async () => {
    // Get unique academic years from current structures
    const uniqueYears = [...new Set(feeStructures.map(s => s.academic_year).filter(Boolean))];
    
    const fromYear = window.prompt(
      `Current sessions in use: ${uniqueYears.join(', ') || 'None'}\n\nEnter OLD session to change FROM:`,
      uniqueYears[0] || '2025-2026'
    );
    if (!fromYear) return;
    
    // Show available sessions from dropdown
    const sessionOptions = academicSessions.map(s => s.session_name).join(', ');
    const toYear = window.prompt(
      `Available sessions: ${sessionOptions || 'None'}\n\nEnter NEW session to change TO:`,
      currentSession?.session_name || '2026-2027'
    );
    if (!toYear) return;
    
    // Count how many will be updated
    const toUpdate = feeStructures.filter(s => s.academic_year === fromYear);
    
    if (toUpdate.length === 0) {
      toast.info(`No fee structures found with session "${fromYear}"`);
      return;
    }
    
    if (!window.confirm(`Change ${toUpdate.length} fee structures from "${fromYear}" to "${toYear}"?`)) {
      return;
    }
    
    try {
      toast.info('Updating fee structures...');
      const res = await axios.post(
        `${API_URL}/api/fee-management/fix-academic-year?from_year=${encodeURIComponent(fromYear)}&to_year=${encodeURIComponent(toYear)}&fix_fee_structures=true&fix_invoices=false`,
        {},
        { headers: getAuthHeader() }
      );
      
      if (res.data.success) {
        toast.success(`Updated ${res.data.fee_structures_updated} fee structures to "${toYear}"`);
        fetchData();
      } else {
        toast.error('Failed: ' + (res.data.errors?.join(', ') || 'Unknown error'));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update fee structures');
    }
  };

  // Fetch Due Fees Summary for Dashboard
  const fetchDueFeesSummary = async () => {
    setLoadingDues(true);
    try {
      const res = await axios.get(`${API_URL}/api/fee-management/due-fees-summary`, { headers: getAuthHeader() });
      setDueFeesSummary(res.data);
    } catch (error) {
      console.error('Failed to fetch due fees:', error);
    } finally {
      setLoadingDues(false);
    }
  };

  // Fetch Pending Invoices for a student
  const fetchPendingInvoices = async (studentId) => {
    try {
      const res = await axios.get(`${API_URL}/api/fee-management/invoices/pending/${studentId}`, { headers: getAuthHeader() });
      setPendingInvoices(res.data);
      setSelectedInvoices([]);
    } catch (error) {
      console.error('Failed to fetch pending invoices:', error);
    }
  };

  // Fetch Student Fee Details
  const fetchStudentFeeDetails = async (studentId) => {
    try {
      const res = await axios.get(`${API_URL}/api/fee-management/student-fee-details/${studentId}`, { headers: getAuthHeader() });
      setStudentFeeDetails(res.data);
    } catch (error) {
      toast.error('Failed to fetch student fee details');
    }
  };

  // Generate Full Year Invoices
  const generateFullYearInvoices = async () => {
    if (!fullYearStudent) return;
    setGeneratingFullYear(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/fee-management/generate-full-year-invoices/${fullYearStudent.id}?apply_discount=true&discount_percent=5`,
        {},
        { headers: getAuthHeader() }
      );
      setFullYearData(res.data);
      toast.success(res.data.message);
      // Refresh student data
      fetchStudentFeeDetails(fullYearStudent.id);
      searchStudents(searchQuery);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate full year invoices');
    } finally {
      setGeneratingFullYear(false);
    }
  };

  // Generate Advance Invoices for custom months (selective fee types - multiple)
  const generateAdvanceInvoices = async () => {
    if (!selectedStudent) return;
    
    // Check at least one fee type is selected
    const selectedTypes = Object.entries(advanceFeeTypes).filter(([_, v]) => v).map(([k, _]) => k);
    if (selectedTypes.length === 0) {
      toast.error('Please select at least one fee type');
      return;
    }
    
    setGeneratingAdvance(true);
    setAdvanceResult(null);
    
    try {
      // Determine fee_type parameter based on selection
      let feeTypeParam = "all";
      if (selectedTypes.length === 1) {
        feeTypeParam = selectedTypes[0];
      } else if (selectedTypes.length === 2) {
        // For 2 selections, we need to call API twice or use a combined param
        // We'll call the API for each selected type
        feeTypeParam = "custom";
      }
      
      let totalGenerated = 0;
      let totalSkipped = 0;
      let allDetails = [];
      let totalAmount = 0;
      
      // Call API for each selected fee type
      for (const feeType of selectedTypes) {
        const res = await axios.post(
          `${API_URL}/api/fee-management/generate-advance-invoices-selective/${selectedStudent.id}?months=${advanceMonths}&fee_type=${feeType}&include_current=true`,
          {},
          { headers: getAuthHeader() }
        );
        totalGenerated += res.data.invoices_generated || 0;
        totalSkipped += res.data.invoices_skipped || 0;
        totalAmount += res.data.total_advance_amount || 0;
        if (res.data.generated_details) {
          allDetails = [...allDetails, ...res.data.generated_details];
        }
      }
      
      const combinedResult = {
        invoices_generated: totalGenerated,
        invoices_skipped: totalSkipped,
        total_advance_amount: totalAmount,
        generated_details: allDetails,
        message: `Generated ${totalGenerated} advance invoices for ${advanceMonths} months. Total: ₹${totalAmount.toLocaleString()}`
      };
      
      setAdvanceResult(combinedResult);
      if (totalGenerated > 0) {
        toast.success(combinedResult.message);
      } else {
        toast.info('No new invoices to generate - may already exist');
      }
      // Refresh student data
      fetchStudentFeeDetails(selectedStudent.id);
      searchStudents(searchQuery);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate advance invoices');
    } finally {
      setGeneratingAdvance(false);
    }
  };

  // Delete wrongly generated one-time fee invoices
  const deleteWrongAdvanceInvoices = async () => {
    if (!selectedStudent) return;
    if (!window.confirm('Are you sure you want to delete wrongly generated one-time fee invoices (Admission, Session, Development fees etc)?')) {
      return;
    }
    setDeletingWrongInvoices(true);
    try {
      const res = await axios.delete(
        `${API_URL}/api/fee-management/delete-wrong-advance-invoices/${selectedStudent.id}`,
        { headers: getAuthHeader() }
      );
      if (res.data.deleted_count > 0) {
        toast.success(`Deleted ${res.data.deleted_count} wrong invoices`);
      } else {
        toast.info('No wrong invoices found to delete');
      }
      // Refresh student data
      fetchStudentFeeDetails(selectedStudent.id);
      searchStudents(searchQuery);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete invoices');
    } finally {
      setDeletingWrongInvoices(false);
    }
  };

  // Process Bulk Payment
  const processBulkPayment = async () => {
    if (!selectedStudent || selectedInvoices.length === 0) return;
    try {
      const res = await axios.post(
        `${API_URL}/api/fee-management/bulk-payment`,
        {
          student_id: selectedStudent.id,
          invoice_ids: selectedInvoices,
          payment_method: paymentForm.payment_mode,
          payment_reference: paymentForm.transaction_id || paymentForm.cheque_number,
          remarks: paymentForm.remarks,
          waive_fine: paymentForm.waive_fine,  // Full fine waive
          fine_discount: paymentForm.fine_discount || 0  // Partial fine discount
        },
        { headers: getAuthHeader() }
      );
      toast.success(res.data.message);
      setBulkPaymentDialog(false);
      setSelectedInvoices([]);
      // Reset fine options after payment
      setPaymentForm(p => ({ ...p, waive_fine: false, fine_discount: 0 }));
      
      // Auto-download PDF receipt (system configured format)
      if (res.data.payment_id) {
        try {
          const pdfResponse = await axios.get(
            `${API_URL}/api/fee-management/payment-receipt/${res.data.payment_id}/pdf`,
            { 
              headers: getAuthHeader(),
              responseType: 'blob'
            }
          );
          
          const blob = new Blob([pdfResponse.data], { type: 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `Receipt_${res.data.receipt_number || res.data.payment_id}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } catch (pdfError) {
          console.error('PDF download failed:', pdfError);
          toast.error('Receipt download failed. You can download it from the payment history.');
        }
      }
      
      // Refresh
      if (selectedStudent) {
        fetchPendingInvoices(selectedStudent.id);
        fetchStudentFeeDetails(selectedStudent.id);
      }
      fetchDueFeesSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment failed');
    }
  };

  const fetchReminderSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/fee-reminder-settings`, { headers: getAuthHeader() });
      if (res.data) {
        setReminderSettings(res.data);
      }
    } catch (error) {
      console.error('Failed to fetch reminder settings');
    }
  };

  const saveReminderSettings = async () => {
    try {
      await axios.post(`${API_URL}/api/fee-reminder-settings`, reminderSettings, { headers: getAuthHeader() });
      toast.success('Reminder settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const sendFeeReminders = async () => {
    setSendingReminders(true);
    try {
      const res = await axios.post(`${API_URL}/api/send-fee-reminders`, {}, { headers: getAuthHeader() });
      toast.success(res.data.message);
    } catch (error) {
      toast.error('Failed to send reminders');
    } finally {
      setSendingReminders(false);
    }
  };

  // Fee Templates Functions
  const fetchFeeTemplates = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/fee-management/fee-head-templates`, { headers: getAuthHeader() });
      setFeeTemplates(res.data);
    } catch (error) {
      console.error('Failed to fetch templates');
    }
  };

  const setupFromTemplates = async () => {
    if (selectedTemplates.length === 0) {
      toast.error('Please select at least one template');
      return;
    }
    setLoadingTemplates(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/fee-management/setup-fee-heads-from-template?template_codes=${selectedTemplates.join(',')}`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(res.data.message);
      setTemplateDialog(false);
      setSelectedTemplates([]);
      fetchData();
    } catch (error) {
      const errorMsg = typeof error.response?.data?.detail === 'string' 
        ? error.response.data.detail 
        : 'Failed to setup fee heads';
      toast.error(errorMsg);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const setupAllTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/fee-management/setup-fee-heads-from-template`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(res.data.message);
      setTemplateDialog(false);
      fetchData();
    } catch (error) {
      const errorMsg = typeof error.response?.data?.detail === 'string' 
        ? error.response.data.detail 
        : 'Failed to setup fee heads';
      toast.error(errorMsg);
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Admission Receipt Functions
  const searchStudentForReceipt = async () => {
    if (!receiptStudentSearch) return;
    try {
      const res = await axios.get(`${API_URL}/api/students?search=${receiptStudentSearch}`, { headers: getAuthHeader() });
      // Handle paginated response
      const students = res.data.items || res.data || [];
      setReceiptSearchResults(students.slice(0, 10));
    } catch (error) {
      toast.error('Search failed');
    }
  };

  const fetchAdmissionReceipt = async (studentId) => {
    try {
      const res = await axios.get(`${API_URL}/api/fee-management/admission-receipt/${studentId}`, { headers: getAuthHeader() });
      setAdmissionReceiptData(res.data);
      setReceiptSearchResults([]);
      setReceiptStudentSearch('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch admission receipt');
    }
  };

  const generateAdmissionInvoice = async (studentId) => {
    try {
      const res = await axios.post(`${API_URL}/api/fee-management/generate-admission-invoice/${studentId}`, {}, { headers: getAuthHeader() });
      if (res.data.status === 'created') {
        toast.success('Admission invoice generated successfully');
        fetchAdmissionReceipt(studentId);
      } else if (res.data.status === 'exists') {
        toast.info('Admission invoice already exists');
      } else {
        toast.info(res.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate invoice');
    }
  };

  const toggleTemplateSelection = (code) => {
    setSelectedTemplates(prev => {
      if (prev.includes(code)) {
        return prev.filter(c => c !== code);
      }
      return [...prev, code];
    });
  };

  useEffect(() => {
    if (activeTab === 'reports') {
      fetchReports(reportMonth);
    }
    if (activeTab === 'settings') {
      fetchReminderSettings();
    }
    if (activeTab === 'setup') {
      fetchFeeTemplates();
    }
  }, [reportMonth, activeTab]);

  const searchStudents = async () => {
    if (!searchQuery) return;
    try {
      const res = await axios.get(`${API_URL}/api/students?search=${searchQuery}`, { headers: getAuthHeader() });
      // Handle both array and {items: [...]} response format
      const students = res.data?.items || res.data || [];
      setSearchResults(Array.isArray(students) ? students.slice(0, 10) : []);
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Search failed');
    }
  };

  const selectStudent = async (student, switchToCollection = false) => {
    setSelectedStudent(student);
    setSearchResults([]);
    setSearchQuery('');
    
    // Switch to Collection tab if called from Defaulters
    if (switchToCollection) {
      setActiveTab('collection');
    }
    
    try {
      // Fetch pending invoices
      const res = await axios.get(`${API_URL}/api/fee-management/invoices/pending/${student.id}`, { headers: getAuthHeader() });
      setPendingInvoices(res.data);
      setSelectedInvoices([]);
      
      // Also fetch student fee details for full year option
      fetchStudentFeeDetails(student.id);
    } catch (error) {
      toast.error('Failed to fetch pending fees');
    }
  };

  const toggleInvoiceSelection = (invoiceId) => {
    setSelectedInvoices(prev => {
      if (prev.includes(invoiceId)) {
        return prev.filter(id => id !== invoiceId);
      }
      return [...prev, invoiceId];
    });
  };

  const calculateTotal = () => {
    return pendingInvoices
      .filter(inv => selectedInvoices.includes(inv.id))
      .reduce((sum, inv) => sum + (inv.balance || inv.net_amount || inv.amount || 0), 0);
  };

  const openCollectionDialog = () => {
    if (selectedInvoices.length === 0) {
      toast.error('Please select at least one fee item');
      return;
    }
    // Reset amount_paid to 0 so user can enter custom amount, or leave empty for full payment
    setPaymentForm(prev => ({ ...prev, amount_paid: 0, waive_fine: false, fine_discount: 0, applied_discount_id: '', applied_discount_amount: 0 }));
    setCollectionDialog(true);
  };

  const collectPayment = async () => {
    // Prevent double-click
    if (isCollectingPayment) {
      toast.warning('Payment already in progress...');
      return;
    }
    
    // Calculate actual amount to pay (after all discounts)
    const totalFine = pendingInvoices.filter(inv => selectedInvoices.includes(inv.id)).reduce((sum, inv) => sum + (inv.late_fine || 0), 0);
    const fineDiscount = paymentForm.waive_fine ? totalFine : (paymentForm.fine_discount || 0);
    const concessionDiscount = paymentForm.applied_discount_amount || 0;
    const totalDiscount = fineDiscount + concessionDiscount;
    const maxAmount = calculateTotal() - totalDiscount;
    
    // Use user-entered amount if available, otherwise use max amount
    const actualAmount = paymentForm.amount_paid > 0 ? paymentForm.amount_paid : maxAmount;
    
    if (actualAmount <= 0) {
      toast.error('Please enter valid amount');
      return;
    }
    
    if (actualAmount > maxAmount) {
      toast.error(`Amount cannot exceed ₹${maxAmount.toLocaleString()}`);
      return;
    }
    
    setIsCollectingPayment(true);  // Start loading
    try {
      const res = await axios.post(`${API_URL}/api/fee-management/collect`, {
        student_id: selectedStudent.id,
        invoice_ids: selectedInvoices,
        ...paymentForm,
        amount_paid: actualAmount,
        discount_amount: concessionDiscount,
        discount_id: paymentForm.applied_discount_id && paymentForm.applied_discount_id !== 'custom' ? paymentForm.applied_discount_id : null,
        discount_reason: concessionDiscount > 0 ? (paymentForm.applied_discount_id === 'custom' ? 'Custom Discount' : discounts.find(d => d.id === paymentForm.applied_discount_id)?.name || 'Discount') : null
      }, { headers: getAuthHeader() });
      
      toast.success(`Payment collected! Receipt: ${res.data.receipt_number}`);
      setCollectionDialog(false);
      
      // Reset discount after payment
      setPaymentForm(p => ({ ...p, applied_discount_id: '', applied_discount_amount: 0, waive_fine: false, fine_discount: 0 }));
      
      // Auto-download PDF receipt (system configured format)
      try {
        const pdfResponse = await axios.get(
          `${API_URL}/api/fee-management/payment-receipt/${res.data.payment_id}/pdf`,
          { 
            headers: getAuthHeader(),
            responseType: 'blob'
          }
        );
        
        // Create download link
        const blob = new Blob([pdfResponse.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Receipt_${res.data.receipt_number}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (pdfError) {
        console.error('PDF download failed:', pdfError);
        toast.error('Receipt download failed. You can download it from the payment history.');
      }
      
      // Refresh
      selectStudent(selectedStudent);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment failed');
    } finally {
      setIsCollectingPayment(false);  // Stop loading
    }
  };

  const createFeeHead = async () => {
    if (!feeHeadForm.name || !feeHeadForm.code) {
      toast.error('Please fill required fields');
      return;
    }
    try {
      if (editingFeeHead) {
        // Update existing fee head
        await axios.put(`${API_URL}/api/fee-management/fee-heads/${editingFeeHead.id}`, feeHeadForm, { headers: getAuthHeader() });
        toast.success('Fee head updated');
      } else {
        // Create new fee head
        await axios.post(`${API_URL}/api/fee-management/fee-heads`, feeHeadForm, { headers: getAuthHeader() });
        toast.success('Fee head created');
      }
      setFeeHeadDialog(false);
      setEditingFeeHead(null);
      setFeeHeadForm({ 
        name: '', code: '', fee_type: 'tuition', fee_category: 'one_time', is_recurring: false, frequency: 'one_time',
        apply_on_new_admission: true, apply_on_re_admission: true, apply_on_promotion: true, apply_on_transfer: true, description: '' 
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    }
  };

  const handleEditFeeHead = (head) => {
    setEditingFeeHead(head);
    // Derive frequency from fee_category if not explicitly set
    const category = FEE_CATEGORIES.find(c => c.value === head.fee_category);
    const derivedFrequency = head.frequency || category?.frequency || head.fee_category || 'one_time';
    
    setFeeHeadForm({
      name: head.name || '',
      code: head.code || '',
      fee_type: head.fee_type || 'tuition',
      fee_category: head.fee_category || 'one_time',
      is_recurring: head.is_recurring || false,
      frequency: derivedFrequency,
      apply_on_new_admission: head.apply_on_new_admission !== false,
      apply_on_re_admission: head.apply_on_re_admission !== false,
      apply_on_promotion: head.apply_on_promotion === true,  // Only true if explicitly set to true
      apply_on_transfer: head.apply_on_transfer !== false,
      description: head.description || ''
    });
    setFeeHeadDialog(true);
  };

  const createStructure = async () => {
    if (!structureForm.class_id || !structureForm.fee_head_id || !structureForm.amount) {
      toast.error('Please fill required fields');
      return;
    }
    try {
      const payload = {
        ...structureForm,
        amount: parseFloat(structureForm.amount),
        late_fine_type: structureForm.late_fine_type || 'per_day',
        late_fine_per_day: parseFloat(structureForm.late_fine_per_day || 0),
        late_fine_monthly: parseFloat(structureForm.late_fine_monthly || 0),
        max_late_fine: parseFloat(structureForm.max_late_fine || 0) || (parseFloat(structureForm.late_fine_per_day || 0) * 30)
      };
      
      if (editingStructure) {
        await axios.put(`${API_URL}/api/fee-management/fee-structures/${editingStructure.id}`, payload, { headers: getAuthHeader() });
        toast.success('Fee structure updated');
      } else {
        await axios.post(`${API_URL}/api/fee-management/fee-structures`, payload, { headers: getAuthHeader() });
        toast.success('Fee structure created');
      }
      setStructureDialog(false);
      setEditingStructure(null);
      setStructureForm({ academic_year: currentSession?.session_name || '', class_id: '', fee_head_id: '', amount: '', due_day: 10, late_fine_type: 'per_day', late_fine_per_day: 0, late_fine_monthly: 0, max_late_fine: 0 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    }
  };

  const handleEditStructure = (structure) => {
    setEditingStructure(structure);
    setStructureForm({
      academic_year: structure.academic_year || currentSession?.session_name || '',
      class_id: structure.class_id || '',
      fee_head_id: structure.fee_head_id || '',
      amount: structure.amount || structure.total_amount || '',
      due_day: structure.due_day || 10,
      late_fine_type: structure.late_fine_type || 'per_day',
      late_fine_per_day: structure.late_fine_per_day || 0,
      late_fine_monthly: structure.late_fine_monthly || 0,
      max_late_fine: structure.max_late_fine || 0
    });
    setStructureDialog(true);
  };

  const handleDeleteStructure = async (structureId) => {
    if (!window.confirm('Are you sure you want to delete this fee structure?')) return;
    try {
      await axios.delete(`${API_URL}/api/fee-management/fee-structures/${structureId}`, { headers: getAuthHeader() });
      toast.success('Fee structure deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const createDiscount = async () => {
    if (!discountForm.name || !discountForm.discount_value) {
      toast.error('Please fill required fields');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/fee-management/discounts`, {
        ...discountForm,
        discount_value: parseFloat(discountForm.discount_value)
      }, { headers: getAuthHeader() });
      toast.success('Discount created successfully');
      setDiscountDialog(false);
      setDiscountForm({ name: '', code: '', discount_type: 'percentage', discount_value: '', applicable_fee_heads: [], description: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create discount');
    }
  };

  const openEditDiscount = (disc) => {
    setEditingDiscount(disc);
    setDiscountForm({
      name: disc.name,
      code: disc.code,
      discount_type: disc.discount_type || 'percentage',
      discount_value: disc.discount_value || '',
      applicable_fee_heads: disc.applicable_fee_heads || [],
      description: disc.description || ''
    });
    setDiscountDialog(true);
  };

  const updateDiscount = async () => {
    if (!discountForm.name || !discountForm.discount_value) {
      toast.error('Please fill required fields');
      return;
    }
    try {
      await axios.put(`${API_URL}/api/fee-management/discounts/${editingDiscount.id}`, null, {
        headers: getAuthHeader(),
        params: {
          name: discountForm.name,
          discount_type: discountForm.discount_type,
          discount_value: parseFloat(discountForm.discount_value),
          description: discountForm.description
        }
      });
      toast.success('Discount updated');
      setDiscountDialog(false);
      setEditingDiscount(null);
      setDiscountForm({ name: '', code: '', discount_type: 'percentage', discount_value: '', applicable_fee_heads: [], description: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update discount');
    }
  };

  const deleteDiscount = async (discountId) => {
    if (!confirm('Delete this discount?')) return;
    try {
      await axios.delete(`${API_URL}/api/fee-management/discounts/${discountId}`, { headers: getAuthHeader() });
      toast.success('Discount deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete discount');
    }
  };

  // Student Fee Category CRUD
  const createFeeCategory = async () => {
    if (!feeCategoryForm.name || !feeCategoryForm.code) {
      toast.error('Please fill name and code');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/fee-management/student-categories`, feeCategoryForm, { headers: getAuthHeader() });
      toast.success('Fee category created');
      setFeeCategoryDialog(false);
      setFeeCategoryForm({ name: '', code: '', discount_percentage: 0, description: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create category');
    }
  };

  const deleteFeeCategory = async (categoryId) => {
    if (!confirm('Delete this fee category?')) return;
    try {
      await axios.delete(`${API_URL}/api/fee-management/student-categories/${categoryId}`, { headers: getAuthHeader() });
      toast.success('Category deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  const openEditCategory = (cat) => {
    setEditingCategory(cat);
    setFeeCategoryForm({
      name: cat.name,
      code: cat.code,
      discount_percentage: cat.discount_percentage || 0,
      description: cat.description || ''
    });
    setFeeCategoryDialog(true);
  };

  const updateFeeCategory = async () => {
    if (!feeCategoryForm.name) {
      toast.error('Please fill name');
      return;
    }
    try {
      await axios.put(`${API_URL}/api/fee-management/student-categories/${editingCategory.id}`, null, {
        headers: getAuthHeader(),
        params: {
          name: feeCategoryForm.name,
          discount_percentage: feeCategoryForm.discount_percentage,
          description: feeCategoryForm.description
        }
      });
      toast.success('Fee category updated');
      setFeeCategoryDialog(false);
      setEditingCategory(null);
      setFeeCategoryForm({ name: '', code: '', discount_percentage: 0, description: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update category');
    }
  };

  // Bulk Assign One-Time Fee
  const openBulkAssignDialog = () => {
    setBulkAssignForm({ class_id: '', fee_head_id: '', amount: '' });
    setBulkAssignStudents([]);
    setBulkAssignDialog(true);
  };

  const fetchStudentsForBulkAssign = async (classId) => {
    if (!classId) {
      setBulkAssignStudents([]);
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/api/students?class_id=${classId}&status=active`, { headers: getAuthHeader() });
      const students = res.data?.items || res.data || [];
      setBulkAssignStudents(students);
    } catch (error) {
      toast.error('Failed to fetch students');
      setBulkAssignStudents([]);
    }
  };

  const handleBulkAssign = async () => {
    if (!bulkAssignForm.class_id || !bulkAssignForm.fee_head_id || !bulkAssignForm.amount) {
      toast.error('Please fill all fields');
      return;
    }
    if (bulkAssignStudents.length === 0) {
      toast.error('No students found in this class');
      return;
    }

    setBulkAssigning(true);
    try {
      const res = await axios.post(`${API_URL}/api/fee-management/bulk-assign`, {
        class_id: bulkAssignForm.class_id,
        fee_head_id: bulkAssignForm.fee_head_id,
        amount: parseFloat(bulkAssignForm.amount),
        academic_year: currentSession?.session_name || ''
      }, { headers: getAuthHeader() });
      
      toast.success(`Fee assigned to ${res.data.assigned_count || bulkAssignStudents.length} students`);
      setBulkAssignDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign fee');
    } finally {
      setBulkAssigning(false);
    }
  };

  // Quick Fee - fetch students when class selected
  const fetchQuickFeeStudents = async (classId) => {
    if (!classId) {
      setQuickFeeStudents([]);
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/api/students?class_id=${classId}&status=active`, { headers: getAuthHeader() });
      const students = res.data?.items || res.data || [];
      setQuickFeeStudents(Array.isArray(students) ? students : []);
    } catch (error) {
      setQuickFeeStudents([]);
    }
  };

  // Quick Fee - create and assign in one step
  const handleQuickFee = async () => {
    if (!quickFeeForm.name || !quickFeeForm.amount || !quickFeeForm.class_id) {
      toast.error('Please fill Fee Name, Amount and select Class');
      return;
    }
    if (quickFeeStudents.length === 0) {
      toast.error('No active students in this class');
      return;
    }
    
    setQuickFeeAssigning(true);
    try {
      const res = await axios.post(`${API_URL}/api/fee-management/quick-fee`, {
        name: quickFeeForm.name,
        amount: parseFloat(quickFeeForm.amount),
        class_id: quickFeeForm.class_id,
        due_date: quickFeeForm.due_date || null
      }, { headers: getAuthHeader() });
      
      toast.success(`"${quickFeeForm.name}" assigned to ${res.data.assigned_count} students!`);
      setQuickFeeDialog(false);
      setQuickFeeForm({ name: '', amount: '', class_id: '', due_date: '' });
      setQuickFeeStudents([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create quick fee');
    } finally {
      setQuickFeeAssigning(false);
    }
  };

  const printReceipt = () => {
    window.print();
  };

  return (
    <DashboardLayout title="Fee Management">
      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); if(v === 'dues') fetchDueFeesSummary(); }} className="space-y-4">
        <TabsList className="flex-wrap">
          <TabsTrigger value="collection"><CreditCard className="h-4 w-4 mr-1" /> Collection</TabsTrigger>
          <TabsTrigger value="history" onClick={() => fetchPaymentHistory(1)}><Receipt className="h-4 w-4 mr-1" /> Payment History</TabsTrigger>
          <TabsTrigger value="dues"><AlertTriangle className="h-4 w-4 mr-1" /> Due Fees</TabsTrigger>
          <TabsTrigger value="setup"><Settings className="h-4 w-4 mr-1" /> Setup</TabsTrigger>
          <TabsTrigger value="reports"><FileText className="h-4 w-4 mr-1" /> Reports</TabsTrigger>
          <TabsTrigger value="reminders"><Bell className="h-4 w-4 mr-1" /> Reminders</TabsTrigger>
        </TabsList>

        {/* Collection Tab */}
        <TabsContent value="collection" className="space-y-4">
          {/* Today's Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg"><DollarSign className="h-5 w-5 text-green-600" /></div>
                  <div>
                    <p className="text-2xl font-bold">₹{(dailyReport?.total_collection || 0).toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">Today Collection</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg"><Receipt className="h-5 w-5 text-blue-600" /></div>
                  <div>
                    <p className="text-2xl font-bold">{dailyReport?.transaction_count || 0}</p>
                    <p className="text-xs text-muted-foreground">Transactions</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-100 rounded-lg"><AlertTriangle className="h-5 w-5 text-red-600" /></div>
                  <div>
                    <p className="text-2xl font-bold">{dueFeesSummary?.summary?.students_with_dues || 0}</p>
                    <p className="text-xs text-muted-foreground">Students with Dues</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg"><Clock className="h-5 w-5 text-amber-600" /></div>
                  <div>
                    <p className="text-2xl font-bold">₹{(dueFeesSummary?.summary?.total_due || 0).toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">Total Pending</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Collection Interface */}
          <Card>
            <CardHeader>
              <CardTitle>Fee Collection</CardTitle>
              <CardDescription>Search student and collect fees</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Search */}
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search student by name, ID, or phone..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && searchStudents()}
                    className="pl-10"
                  />
                </div>
                <Button onClick={searchStudents}>Search</Button>
              </div>

              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="border rounded-lg divide-y">
                  {searchResults.map(student => (
                    <div
                      key={student.id}
                      className="p-3 hover:bg-muted cursor-pointer flex items-center justify-between"
                      onClick={() => selectStudent(student)}
                    >
                      <div>
                        <p className="font-medium">{student.name}</p>
                        <p className="text-sm text-muted-foreground">{student.student_id} • {student.class_name}</p>
                      </div>
                      <Button size="sm" variant="outline">Select</Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Selected Student */}
              {selectedStudent && (
                <div className="border rounded-lg p-4 bg-muted/50">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-lg">{selectedStudent.name}</h3>
                      <p className="text-sm text-muted-foreground">
                        {selectedStudent.student_id} • {selectedStudent.class_name} • Father: {selectedStudent.father_name}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => navigate(`/student-ledger/${selectedStudent.id}`)}
                        data-testid="view-student-ledger"
                      >
                        <FileText className="h-4 w-4 mr-1" /> View Ledger
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => { setSelectedStudent(null); setPendingInvoices([]); }}>
                        Clear
                      </Button>
                    </div>
                  </div>

                  {/* Pending Fees */}
                  <div className="space-y-2">
                    <h4 className="font-medium text-sm">Pending Dues:</h4>
                    {pendingInvoices.length === 0 ? (
                      <p className="text-sm text-green-600 flex items-center gap-2">
                        <CheckCircle className="h-4 w-4" /> No pending fees
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {pendingInvoices.map(inv => (
                          <div
                            key={inv.id}
                            className={`p-3 border rounded-lg flex items-center gap-3 cursor-pointer transition-colors ${
                              selectedInvoices.includes(inv.id) ? 'bg-primary/10 border-primary' : 'bg-white'
                            }`}
                            onClick={() => toggleInvoiceSelection(inv.id)}
                          >
                            <Checkbox checked={selectedInvoices.includes(inv.id)} />
                            <div className="flex-1">
                              <p className="font-medium">
                                {inv.fee_head_name || (inv.items?.length > 0 
                                  ? inv.items.map(i => i.fee_name).join(', ')
                                  : inv.invoice_type === 'promotion' ? 'Promotion Fees' 
                                  : inv.invoice_type === 'hostel' ? 'Hostel Fee'
                                  : inv.invoice_type === 'transport' ? 'Transport Fee'
                                  : 'Fee')}
                              </p>
                              <p className="text-xs text-muted-foreground">{inv.month || inv.invoice_type}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold">₹{(inv.balance || inv.total_amount || inv.net_amount || inv.amount || 0).toLocaleString()}</p>
                              {inv.late_fine > 0 && (
                                <p className="text-xs text-red-500">+₹{inv.late_fine} fine</p>
                              )}
                            </div>
                            <Badge variant={inv.status === 'overdue' ? 'destructive' : 'secondary'}>
                              {inv.status}
                            </Badge>
                          </div>
                        ))}

                        <div className="flex items-center justify-between pt-4 border-t">
                          <div>
                            <p className="text-sm text-muted-foreground">Selected: {selectedInvoices.length} items</p>
                            <p className="text-xl font-bold">Total: ₹{(calculateTotal() || 0).toLocaleString()}</p>
                            {/* Show total fine if any */}
                            {pendingInvoices.filter(inv => selectedInvoices.includes(inv.id) && inv.late_fine > 0).length > 0 && (
                              <p className="text-sm text-red-500">
                                (includes ₹{pendingInvoices.filter(inv => selectedInvoices.includes(inv.id)).reduce((sum, inv) => sum + (inv.late_fine || 0), 0).toLocaleString()} fine)
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button variant="outline" onClick={() => setSelectedInvoices(pendingInvoices.map(i => i.id))}>
                              Select All
                            </Button>
                            <Button onClick={openCollectionDialog} disabled={selectedInvoices.length === 0}>
                              <CreditCard className="h-4 w-4 mr-2" /> Collect Payment
                            </Button>
                          </div>
                        </div>
                        
                        {/* Fine Waive/Discount Option - Show only if there's fine in selected invoices */}
                        {pendingInvoices.filter(inv => selectedInvoices.includes(inv.id) && inv.late_fine > 0).length > 0 && (
                          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
                            <p className="text-sm font-medium text-amber-800">
                              Total Late Fine: ₹{pendingInvoices.filter(inv => selectedInvoices.includes(inv.id)).reduce((sum, inv) => sum + (inv.late_fine || 0), 0).toLocaleString()}
                            </p>
                            
                            {/* Full Waive Option */}
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={paymentForm.waive_fine}
                                onChange={e => {
                                  const totalFine = pendingInvoices.filter(inv => selectedInvoices.includes(inv.id)).reduce((sum, inv) => sum + (inv.late_fine || 0), 0);
                                  const newTotal = e.target.checked ? (calculateTotal() - totalFine) : calculateTotal();
                                  setPaymentForm(p => ({ 
                                    ...p, 
                                    waive_fine: e.target.checked,
                                    fine_discount: e.target.checked ? 0 : p.fine_discount,
                                    amount_paid: newTotal > 0 ? newTotal : 0
                                  }));
                                }}
                                className="w-4 h-4 text-amber-600 border-amber-300 rounded focus:ring-amber-500"
                              />
                              <span className="text-amber-800 font-medium">
                                Full Fine Waive (100%)
                              </span>
                            </label>
                            
                            {/* Partial Discount Option - Only show if not full waive */}
                            {!paymentForm.waive_fine && (
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-amber-700">Or Discount:</span>
                                <input
                                  type="number"
                                  min="0"
                                  max={pendingInvoices.filter(inv => selectedInvoices.includes(inv.id)).reduce((sum, inv) => sum + (inv.late_fine || 0), 0)}
                                  value={paymentForm.fine_discount || ''}
                                  onChange={e => {
                                    const discount = parseFloat(e.target.value) || 0;
                                    const newTotal = calculateTotal() - discount;
                                    setPaymentForm(p => ({ 
                                      ...p, 
                                      fine_discount: discount,
                                      amount_paid: newTotal > 0 ? newTotal : 0
                                    }));
                                  }}
                                  placeholder="Enter amount"
                                  className="w-32 px-2 py-1 text-sm border border-amber-300 rounded focus:ring-amber-500 focus:border-amber-500"
                                />
                                <span className="text-sm text-amber-600">₹</span>
                              </div>
                            )}
                            
                            {/* Show updated calculation */}
                            {(paymentForm.waive_fine || paymentForm.fine_discount > 0) && (
                              <p className="text-xs text-amber-600 border-t border-amber-200 pt-2">
                                {paymentForm.waive_fine ? (
                                  <>✓ Full fine waived. New Total: ₹{(calculateTotal() - pendingInvoices.filter(inv => selectedInvoices.includes(inv.id)).reduce((sum, inv) => sum + (inv.late_fine || 0), 0)).toLocaleString()}</>
                                ) : (
                                  <>✓ Fine discount: ₹{paymentForm.fine_discount.toLocaleString()}. New Total: ₹{(calculateTotal() - paymentForm.fine_discount).toLocaleString()}</>
                                )}
                              </p>
                            )}
                          </div>
                        )}

                        {/* Full Year Payment Option */}
                        {studentFeeDetails?.payment_options?.pay_full_year?.available && (
                          <div className="mt-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-lg shadow-sm">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-bold text-green-800 flex items-center gap-2 text-lg">
                                  <Zap className="h-5 w-5 text-yellow-500" /> 🎉 Full Year Advance Payment - Save 5%!
                                </p>
                                <p className="text-sm text-green-700 mt-1">
                                  Pay the entire year's fee at once and save ₹{studentFeeDetails.payment_options.pay_full_year.discount_amount?.toLocaleString()}!
                                </p>
                                <p className="text-green-600 mt-2">
                                  <span className="line-through text-gray-500">₹{studentFeeDetails.payment_options.pay_full_year.original_amount?.toLocaleString()}</span>
                                  <span className="text-2xl font-bold ml-2">₹{studentFeeDetails.payment_options.pay_full_year.final_amount?.toLocaleString()}</span>
                                </p>
                              </div>
                              <Button 
                                size="lg"
                                className="bg-green-600 hover:bg-green-700 text-white shadow-lg"
                                onClick={() => {
                                  setFullYearStudent(selectedStudent);
                                  setFullYearDialog(true);
                                }}
                              >
                                <Calendar className="h-5 w-5 mr-2" />
                                Generate Full Year Invoices
                              </Button>
                            </div>
                          </div>
                        )}
                        
                        {/* Show message if full year already generated or no monthly fees */}
                        {studentFeeDetails && !studentFeeDetails?.payment_options?.pay_full_year?.available && (
                          <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                            <p className="text-sm text-gray-600 flex items-center gap-2">
                              <Calendar className="h-4 w-4" />
                              {studentFeeDetails?.summary?.monthly_pending > 0 
                                ? "Full year invoices already generated for this student"
                                : "No monthly fees pending for full year advance payment"}
                            </p>
                          </div>
                        )}
                        
                        {/* Advance Invoice Generation - Custom Months */}
                        {selectedStudent && (
                          <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl">
                            <div className="flex items-center gap-2 mb-3">
                              <Calendar className="h-5 w-5 text-blue-600" />
                              <h3 className="font-semibold text-blue-800">Generate Advance Invoices</h3>
                            </div>
                            <p className="text-sm text-blue-700 mb-4">
                              Generate advance invoices for Transport or Hostel fees. 
                              <span className="text-xs text-blue-500 block mt-1">Note: Tuition fees are auto-generated for the full year during admission/promotion.</span>
                            </p>
                            
                            <div className="flex flex-wrap items-center gap-4 mb-4">
                              <Label className="text-sm text-gray-700 font-medium">Select Fee Types:</Label>
                              <div className="flex items-center gap-4">
                                <label className="flex items-center gap-2 cursor-pointer">
                                  <Checkbox 
                                    checked={advanceFeeTypes.transport}
                                    onCheckedChange={(checked) => setAdvanceFeeTypes(prev => ({...prev, transport: checked}))}
                                  />
                                  <span className="text-sm">Transport</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                  <Checkbox 
                                    checked={advanceFeeTypes.hostel}
                                    onCheckedChange={(checked) => setAdvanceFeeTypes(prev => ({...prev, hostel: checked}))}
                                  />
                                  <span className="text-sm">Hostel</span>
                                </label>
                              </div>
                            </div>
                            
                            <div className="flex flex-wrap items-center gap-4">
                              <div className="flex items-center gap-2">
                                <Label className="text-sm text-gray-700">Months:</Label>
                                <Select value={advanceMonths.toString()} onValueChange={(v) => setAdvanceMonths(parseInt(v))}>
                                  <SelectTrigger className="w-24">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {[2,3,4,5,6,7,8,9,10,11,12].map(m => (
                                      <SelectItem key={m} value={m.toString()}>{m} Months</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              
                              <Button 
                                onClick={generateAdvanceInvoices}
                                disabled={generatingAdvance || !Object.values(advanceFeeTypes).some(v => v)}
                                className="bg-blue-600 hover:bg-blue-700"
                              >
                                {generatingAdvance ? (
                                  <>
                                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                    Generating...
                                  </>
                                ) : (
                                  <>
                                    <Plus className="h-4 w-4 mr-2" />
                                    Generate {advanceMonths} Months Advance
                                  </>
                                )}
                              </Button>
                              
                              <Button 
                                onClick={deleteWrongAdvanceInvoices}
                                disabled={deletingWrongInvoices}
                                variant="destructive"
                                size="sm"
                                className="bg-red-500 hover:bg-red-600"
                              >
                                {deletingWrongInvoices ? (
                                  <>
                                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                    Deleting...
                                  </>
                                ) : (
                                  <>
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete Wrong Invoices
                                  </>
                                )}
                              </Button>
                            </div>
                            
                            {/* Show result after generation */}
                            {advanceResult && (
                              <div className="mt-4 p-3 bg-white rounded-lg border border-blue-200">
                                <p className="text-sm font-medium text-green-700 flex items-center gap-2">
                                  <CheckCircle className="h-4 w-4" />
                                  {advanceResult.message}
                                </p>
                                {advanceResult.generated_details?.length > 0 && (
                                  <div className="mt-2 text-xs text-gray-600">
                                    <p className="font-medium">Generated Invoices:</p>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-1">
                                      {advanceResult.generated_details.map((inv, idx) => (
                                        <div key={idx} className="bg-gray-50 p-2 rounded">
                                          <p className="font-medium">{inv.month}</p>
                                          <p className="text-xs text-gray-500">{inv.fee_type || inv.fee_head}</p>
                                          <p>₹{inv.amount?.toLocaleString()}</p>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payment History Tab */}
        <TabsContent value="history" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5" />
                Payment History & Receipt Reprint
              </CardTitle>
              <CardDescription>View all payments and reprint receipts</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Date Range Filter */}
              <div className="flex flex-wrap gap-4 items-end">
                <div>
                  <Label>From Date</Label>
                  <Input 
                    type="date" 
                    value={historyDateFrom}
                    onChange={e => setHistoryDateFrom(e.target.value)}
                    className="w-40"
                  />
                </div>
                <div>
                  <Label>To Date</Label>
                  <Input 
                    type="date" 
                    value={historyDateTo}
                    onChange={e => setHistoryDateTo(e.target.value)}
                    className="w-40"
                  />
                </div>
                <div className="flex-1">
                  <Label>Search Student</Label>
                  <Input 
                    placeholder="Student name, ID, or receipt number..."
                    value={historySearchQuery}
                    onChange={e => setHistorySearchQuery(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && fetchPaymentHistory(1)}
                  />
                </div>
                <Button onClick={() => fetchPaymentHistory(1)} disabled={paymentHistoryLoading}>
                  <Search className="h-4 w-4 mr-1" /> Search
                </Button>
              </div>

              {/* Payment History Table */}
              {paymentHistoryLoading ? (
                <div className="text-center py-8 text-muted-foreground">Loading...</div>
              ) : paymentHistory.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No payments found for selected date range
                </div>
              ) : (
                <>
                  <div className="border rounded-lg overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted">
                        <tr>
                          <th className="p-3 text-left">Date</th>
                          <th className="p-3 text-left">Receipt No</th>
                          <th className="p-3 text-left">Student</th>
                          <th className="p-3 text-left">Class</th>
                          <th className="p-3 text-left">Fee Type</th>
                          <th className="p-3 text-right">Amount</th>
                          <th className="p-3 text-right text-amber-600">Fine Waiver</th>
                          <th className="p-3 text-right text-orange-600">Concession</th>
                          <th className="p-3 text-center">Mode</th>
                          <th className="p-3 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {paymentHistory.map(payment => (
                          <tr key={payment.id} className="hover:bg-muted/50">
                            <td className="p-3">{payment.payment_date || payment.created_at?.slice(0, 10)}</td>
                            <td className="p-3 font-medium text-primary">{payment.receipt_number || '-'}</td>
                            <td className="p-3">
                              <p className="font-medium">{payment.student_name}</p>
                              <p className="text-xs text-muted-foreground">{payment.student_id_display || payment.admission_number}</p>
                            </td>
                            <td className="p-3">{payment.class_name}</td>
                            <td className="p-3">{payment.fee_head_name || payment.fee_type || 'Fee'}</td>
                            <td className="p-3 text-right font-medium text-green-600">₹{(payment.amount_paid || payment.amount || 0).toLocaleString()}</td>
                            <td className="p-3 text-right">
                              {(payment.fine_waived || 0) > 0 ? (
                                <span className="text-amber-600 font-medium">
                                  -₹{(payment.fine_waived || 0).toLocaleString()}
                                </span>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </td>
                            <td className="p-3 text-right">
                              {(payment.discount_amount || 0) > 0 ? (
                                <span className="text-orange-600 font-medium">
                                  -₹{(payment.discount_amount || 0).toLocaleString()}
                                  {payment.discount_reason && (
                                    <span className="block text-xs text-muted-foreground">{payment.discount_reason}</span>
                                  )}
                                </span>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </td>
                            <td className="p-3 text-center">
                              <span className={`px-2 py-1 rounded text-xs ${
                                payment.payment_mode === 'cash' ? 'bg-green-100 text-green-700' :
                                payment.payment_mode === 'upi' ? 'bg-purple-100 text-purple-700' :
                                payment.payment_mode === 'card' ? 'bg-blue-100 text-blue-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {payment.payment_mode?.toUpperCase() || 'CASH'}
                              </span>
                            </td>
                            <td className="p-3 text-center">
                              <div className="flex gap-1 justify-center">
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => downloadReceiptPDF(payment.id, payment.receipt_number)}
                                  data-testid={`download-receipt-${payment.id}`}
                                >
                                  <Download className="h-3 w-3 mr-1" /> PDF
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => {
                                    viewReceipt(payment.invoice_id || payment.id);
                                  }}
                                  data-testid={`view-receipt-history-${payment.id}`}
                                >
                                  <Eye className="h-3 w-3" />
                                </Button>
                                {user?.role === 'school_admin' && (
                                  <Button 
                                    size="sm" 
                                    variant="ghost"
                                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => previewDeletePayment(payment)}
                                    disabled={deletingPayment}
                                    data-testid={`delete-payment-${payment.id}`}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">
                      Page {historyPage} of {historyTotalPages}
                    </p>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        disabled={historyPage <= 1}
                        onClick={() => fetchPaymentHistory(historyPage - 1)}
                      >
                        Previous
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        disabled={historyPage >= historyTotalPages}
                        onClick={() => fetchPaymentHistory(historyPage + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Due Fees Dashboard Tab */}
        <TabsContent value="dues" className="space-y-4">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-red-200 bg-red-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-100 rounded-lg"><AlertTriangle className="h-5 w-5 text-red-600" /></div>
                  <div>
                    <p className="text-2xl font-bold text-red-600">₹{(dueFeesSummary?.summary?.total_due || 0).toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">Total Due</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-orange-200 bg-orange-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg"><Clock className="h-5 w-5 text-orange-600" /></div>
                  <div>
                    <p className="text-2xl font-bold text-orange-600">₹{(dueFeesSummary?.summary?.total_overdue || 0).toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">Overdue Amount</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg"><FileText className="h-5 w-5 text-amber-600" /></div>
                  <div>
                    <p className="text-2xl font-bold">{dueFeesSummary?.summary?.pending_invoices || 0}</p>
                    <p className="text-xs text-muted-foreground">Pending Invoices</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg"><Users className="h-5 w-5 text-blue-600" /></div>
                  <div>
                    <p className="text-2xl font-bold">{dueFeesSummary?.summary?.students_with_dues || 0}</p>
                    <p className="text-xs text-muted-foreground">Students with Dues</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Generate Monthly Invoices Button */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between py-3">
              <CardTitle className="text-lg">Generate Monthly Invoices</CardTitle>
              <div className="flex items-center gap-2">
                <Input 
                  type="month" 
                  value={generateMonth} 
                  onChange={(e) => setGenerateMonth(e.target.value)}
                  className="w-40"
                />
                <Button onClick={generateMonthlyInvoices} disabled={generating}>
                  {generating ? 'Generating...' : 'Generate'}
                </Button>
              </div>
            </CardHeader>
          </Card>

          {/* Fee Type Summary */}
          {dueFeesSummary?.by_fee_type && Object.keys(dueFeesSummary.by_fee_type).length > 0 && (
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-lg">Due Amount by Fee Type</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(dueFeesSummary.by_fee_type).map(([feeName, data]) => (
                    <div key={feeName} className="p-3 border rounded-lg">
                      <p className="font-medium text-sm">{feeName}</p>
                      <p className="text-lg font-bold">₹{data.total?.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">{data.count} invoices</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Full Year Advance Payment Info Card */}
          <Card className="border-2 border-green-300 bg-gradient-to-r from-green-50 to-emerald-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-green-100 rounded-full">
                    <Zap className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-green-800 text-lg">💰 Full Year Advance Payment Option</h3>
                    <p className="text-sm text-green-700">
                      Parents can pay the entire year's monthly fees in advance and get <span className="font-bold">5% discount!</span>
                    </p>
                    <p className="text-xs text-green-600 mt-1">
                      To use: Go to Collection tab → Search student → Select student → Click "Generate Full Year Invoices"
                    </p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  className="border-green-500 text-green-700 hover:bg-green-100"
                  onClick={() => setActiveTab('collection')}
                >
                  Go to Collection
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Top Defaulters List */}
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                Students with Due Fees
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingDues ? (
                <p className="text-center py-8 text-muted-foreground">Loading...</p>
              ) : !dueFeesSummary?.top_defaulters?.length ? (
                <p className="text-center py-8 text-muted-foreground">No due fees! 🎉</p>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {dueFeesSummary.top_defaulters.map((student, idx) => (
                    <div 
                      key={student.student_id} 
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50"
                    >
                      <div 
                        className="flex items-center gap-3 flex-1 cursor-pointer"
                        onClick={() => {
                          setSelectedStudent({id: student.student_id, name: student.student_name});
                          fetchPendingInvoices(student.student_id);
                          fetchStudentFeeDetails(student.student_id);
                          setActiveTab('collection');
                        }}
                      >
                        <span className="w-6 h-6 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-xs font-bold">
                          {idx + 1}
                        </span>
                        <div>
                          <p className="font-medium">{student.student_name}</p>
                          <p className="text-xs text-muted-foreground">{student.class} | {student.pending_count} invoices</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="font-bold text-red-600">₹{student.total_due?.toLocaleString()}</p>
                          {student.overdue > 0 && (
                            <Badge variant="destructive" className="text-xs">₹{student.overdue?.toLocaleString()} overdue</Badge>
                          )}
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/student-ledger/${student.student_id}`);
                          }}
                          data-testid={`view-ledger-${student.student_id}`}
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Setup Tab */}
        <TabsContent value="setup" className="space-y-4">
          {/* Quick Setup Banner */}
          {feeHeads.length === 0 && (
            <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg"><Zap className="h-5 w-5 text-blue-600" /></div>
                  <div>
                    <p className="font-medium">Quick Setup</p>
                    <p className="text-sm text-muted-foreground">Create standard fee heads from predefined templates in one click</p>
                  </div>
                </div>
                <Button onClick={() => { fetchFeeTemplates(); setTemplateDialog(true); }} variant="default">
                  <BookOpen className="h-4 w-4 mr-2" /> Use Templates
                </Button>
              </CardContent>
            </Card>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            {/* Fee Heads */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-base">Fee Heads</CardTitle>
                  <CardDescription>Define fee categories with admission rules</CardDescription>
                </div>
                <div className="flex gap-2">
                  {/* Templates button only shows when NO fee heads exist - for new schools */}
                  {feeHeads.length === 0 && (
                    <Button size="sm" variant="outline" onClick={() => { fetchFeeTemplates(); setTemplateDialog(true); }}>
                      <BookOpen className="h-4 w-4 mr-1" /> Templates
                    </Button>
                  )}
                  <Button size="sm" onClick={() => { setEditingFeeHead(null); setFeeHeadDialog(true); }}>
                    <Plus className="h-4 w-4 mr-1" /> Add
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="max-h-80 overflow-y-auto">
                  {feeHeads.length === 0 ? (
                    <p className="p-4 text-center text-muted-foreground">No fee heads created</p>
                  ) : (
                    <table className="w-full">
                      <thead className="bg-muted/50 sticky top-0">
                        <tr>
                          <th className="p-2 text-left text-xs">Name</th>
                          <th className="p-2 text-left text-xs">Code</th>
                          <th className="p-2 text-left text-xs">Schedule</th>
                          <th className="p-2 text-center text-xs">Applied To</th>
                          <th className="p-2 text-center text-xs">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {feeHeads.map(head => (
                          <tr key={head.id} className="border-t">
                            <td className="p-2 text-sm">{head.name}</td>
                            <td className="p-2 text-sm font-mono">{head.code}</td>
                            <td className="p-2">
                              <Badge variant="outline" className="text-xs capitalize">
                                {(head.fee_category || 'other').replace('_', ' ')}
                              </Badge>
                            </td>
                            <td className="p-2">
                              <div className="flex gap-1 justify-center flex-wrap">
                                {head.apply_on_new_admission !== false && (
                                  <Badge variant="secondary" className="text-[10px] px-1">New</Badge>
                                )}
                                {head.apply_on_re_admission !== false && (
                                  <Badge variant="secondary" className="text-[10px] px-1">Re-Adm</Badge>
                                )}
                                {head.apply_on_promotion === true && (
                                  <Badge variant="outline" className="text-[10px] px-1 border-green-500 text-green-600">Promo</Badge>
                                )}
                                {head.apply_on_transfer !== false && (
                                  <Badge variant="secondary" className="text-[10px] px-1">Transfer</Badge>
                                )}
                              </div>
                            </td>
                            <td className="p-2 text-center">
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => handleEditFeeHead(head)}
                                className="h-7 px-2"
                              >
                                <Pencil className="h-3 w-3" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Fee Structures */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-base">Fee Structures</CardTitle>
                  <CardDescription>Class-wise fee amounts</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={bulkChangeStructureSession} className="text-blue-600 border-blue-300 hover:bg-blue-50">
                    <RefreshCw className="h-4 w-4 mr-1" /> Change All Sessions
                  </Button>
                  <Button size="sm" onClick={() => { setEditingStructure(null); setStructureDialog(true); }}>
                    <Plus className="h-4 w-4 mr-1" /> Add
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="max-h-96 overflow-y-auto">
                  {feeStructures.length === 0 ? (
                    <p className="p-4 text-center text-muted-foreground">No structures created</p>
                  ) : (
                    <div className="divide-y">
                      {feeStructures.map(s => (
                        <div key={s.id} className="p-3 hover:bg-muted/30">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-sm">{s.class_name}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-bold text-green-600">₹{(s.amount || s.total_amount || 0).toLocaleString()}</span>
                              <Button size="sm" variant="ghost" className="h-6 px-1" onClick={() => handleEditStructure(s)}>
                                <Pencil className="h-3 w-3" />
                              </Button>
                              <Button size="sm" variant="ghost" className="h-6 px-1 text-red-500 hover:text-red-700" onClick={() => handleDeleteStructure(s.id)}>
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                          {s.components && s.components.length > 0 ? (
                            <div className="grid grid-cols-2 gap-1 text-xs text-muted-foreground">
                              {s.components.map((comp, idx) => (
                                <div key={idx} className="flex justify-between bg-muted/50 rounded px-2 py-1">
                                  <span>{comp.name || comp.fee_head_name}</span>
                                  <span>₹{(comp.amount || 0).toLocaleString()}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-xs text-muted-foreground">
                              {s.fee_head_name} - ₹{(s.amount || 0).toLocaleString()}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Admission Receipt & Discounts */}
          <div className="grid md:grid-cols-2 gap-4">
            {/* Admission Receipt */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Receipt className="h-4 w-4" /> Admission Receipt
                </CardTitle>
                <CardDescription>View combined admission fee receipt for a student</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search student..."
                      value={receiptStudentSearch}
                      onChange={e => setReceiptStudentSearch(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && searchStudentForReceipt()}
                      className="pl-10"
                    />
                  </div>
                  <Button onClick={searchStudentForReceipt} size="sm">Search</Button>
                </div>
                
                {receiptSearchResults.length > 0 && (
                  <div className="border rounded-lg divide-y max-h-40 overflow-y-auto">
                    {receiptSearchResults.map(s => (
                      <div key={s.id} className="p-2 hover:bg-muted cursor-pointer flex items-center justify-between" onClick={() => fetchAdmissionReceipt(s.id)}>
                        <div>
                          <p className="text-sm font-medium">{s.name}</p>
                          <p className="text-xs text-muted-foreground">{s.student_id} • {s.class_name}</p>
                        </div>
                        <Button size="sm" variant="ghost"><Eye className="h-4 w-4" /></Button>
                      </div>
                    ))}
                  </div>
                )}

                {admissionReceiptData && (
                  <div className="border rounded-lg p-3 bg-muted/30 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{admissionReceiptData.student?.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {admissionReceiptData.student?.admission_number} • {admissionReceiptData.student?.class}
                        </p>
                      </div>
                      <Badge>{admissionReceiptData.student?.admission_type || 'new'}</Badge>
                    </div>
                    
                    <div className="text-sm space-y-1">
                      <div className="flex justify-between">
                        <span>Total Fees:</span>
                        <span className="font-medium">₹{(admissionReceiptData.summary?.total_amount || 0).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-green-600">
                        <span>Paid:</span>
                        <span>₹{(admissionReceiptData.summary?.total_paid || 0).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-red-600">
                        <span>Balance:</span>
                        <span>₹{(admissionReceiptData.summary?.total_balance || 0).toLocaleString()}</span>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="flex-1" onClick={() => setAdmissionReceiptDialog(true)}>
                        <Eye className="h-4 w-4 mr-1" /> View Details
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => generateAdmissionInvoice(admissionReceiptData.student?.id)}>
                        <FileText className="h-4 w-4 mr-1" /> Generate Invoice
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Discounts */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-base">Discounts & Concessions</CardTitle>
                  <CardDescription>Fee waivers and scholarships</CardDescription>
                </div>
                <Button size="sm" onClick={() => setDiscountDialog(true)}>
                  <Plus className="h-4 w-4 mr-1" /> Add Discount
                </Button>
              </CardHeader>
              <CardContent>
                {discounts.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">No discounts configured</p>
                ) : (
                  <div className="grid gap-3">
                    {discounts.map(d => (
                      <div key={d.id} className="p-3 border rounded-lg flex items-center justify-between">
                        <div>
                          <p className="font-medium">{d.name}</p>
                          <p className="text-sm text-muted-foreground">{d.code} {d.description ? `- ${d.description}` : ''}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge>
                            {d.discount_type === 'percentage' ? `${d.discount_value}%` : `₹${d.discount_value}`}
                          </Badge>
                          <Button size="sm" variant="ghost" onClick={() => openEditDiscount(d)} data-testid={`edit-discount-${d.id}`}>
                            <Pencil className="h-4 w-4 text-blue-500" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => deleteDiscount(d.id)} data-testid={`delete-discount-${d.id}`}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-muted-foreground mt-3 p-2 bg-blue-50 rounded">
                  <strong>Where to Apply:</strong> When assigning fees during Student Admission, manually select and apply. 
                  For auto discount, use "Student Fee Categories".
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Student Fee Categories */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base">Student Fee Categories</CardTitle>
                <CardDescription>Categories for student admission with auto-discount (General, Staff Ward, Scholarship etc.)</CardDescription>
              </div>
              <Button size="sm" onClick={() => setFeeCategoryDialog(true)}>
                <Plus className="h-4 w-4 mr-1" /> Add Category
              </Button>
            </CardHeader>
            <CardContent>
              {studentFeeCategories.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">No fee categories configured</p>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {studentFeeCategories.map(cat => (
                    <div key={cat.id} className="p-3 border rounded-lg flex items-center justify-between">
                      <div>
                        <p className="font-medium">{cat.name}</p>
                        <p className="text-xs text-muted-foreground">{cat.description || cat.code}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={cat.discount_percentage > 0 ? "default" : "secondary"}>
                          {cat.discount_percentage > 0 ? `${cat.discount_percentage}% off` : 'No discount'}
                        </Badge>
                        <Button size="sm" variant="ghost" onClick={() => openEditCategory(cat)} data-testid={`edit-category-${cat.code}`}>
                          <Pencil className="h-4 w-4 text-blue-500" />
                        </Button>
                        {cat.code !== 'general' && (
                          <Button size="sm" variant="ghost" onClick={() => deleteFeeCategory(cat.id)} data-testid={`delete-category-${cat.code}`}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Fee - One-click fee assignment */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Zap className="h-4 w-4 text-yellow-500" />
                Quick Fee
              </CardTitle>
              <CardDescription>Create and assign a fee to an entire class in one step (e.g., Saraswati Puja, Picnic, Event)</CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => setQuickFeeDialog(true)} variant="outline">
                <Plus className="h-4 w-4 mr-2" />
                Create Quick Fee
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports" className="space-y-4">
          {/* Report Controls */}
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Label>From:</Label>
              <Input 
                type="date" 
                value={reportFromDate} 
                onChange={e => setReportFromDate(e.target.value)} 
                className="w-40"
                data-testid="report-from-date"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label>To:</Label>
              <Input 
                type="date" 
                value={reportToDate} 
                onChange={e => setReportToDate(e.target.value)} 
                className="w-40"
                data-testid="report-to-date"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label>Fee Head:</Label>
              <select
                value={reportFeeHead}
                onChange={e => setReportFeeHead(e.target.value)}
                className="border rounded px-2 py-1.5 text-sm bg-background w-48"
                data-testid="report-fee-head-filter"
              >
                <option value="">All Fee Heads</option>
                {feeHeads.map(fh => (
                  <option key={fh.id} value={fh.name}>{fh.name}</option>
                ))}
              </select>
            </div>
            <Button onClick={checkAndGenerateInvoices} variant="outline">
              <Plus className="h-4 w-4 mr-2" /> Generate Monthly Invoices
            </Button>
            <Button onClick={checkAndApplyFines} variant="outline" className="bg-amber-100 text-amber-700 border-amber-400 hover:bg-amber-200 font-medium">
              <Clock className="h-4 w-4 mr-2" /> Apply Late Fines
            </Button>
            <Button onClick={runMissedCronJobs} variant="outline" className="bg-purple-100 text-purple-700 border-purple-400 hover:bg-purple-200 font-medium">
              <RefreshCw className="h-4 w-4 mr-2" /> Run All Cron Jobs
            </Button>
            <Button onClick={fixDueDatesAndFines} variant="outline" className="bg-red-100 text-red-700 border-red-400 hover:bg-red-200 font-medium">
              <AlertTriangle className="h-4 w-4 mr-2" /> Fix Due Dates & Fines
            </Button>
            <Button onClick={cleanupWrongAdmissionInvoices} variant="outline" className="bg-orange-100 text-orange-700 border-orange-400 hover:bg-orange-200 font-medium">
              <Trash2 className="h-4 w-4 mr-2" /> Cleanup Wrong Invoices
            </Button>
            <Button onClick={fixAcademicYear} variant="outline" className="bg-blue-100 text-blue-700 border-blue-400 hover:bg-blue-200 font-medium">
              <Calendar className="h-4 w-4 mr-2" /> Fix Academic Year
            </Button>
          </div>

          {/* Excel Export Options */}
          <div className="flex items-center gap-2 flex-wrap p-3 bg-green-50 rounded border border-green-200">
            <span className="text-sm font-medium text-green-800 mr-2">Excel Export:</span>
            <Button onClick={() => exportReportsToExcel('all')} variant="outline" size="sm" className="bg-green-100 text-green-700 border-green-400 hover:bg-green-200" data-testid="export-all-btn">
              <Download className="h-3 w-3 mr-1" /> All Reports
            </Button>
            <Button onClick={() => exportReportsToExcel('payment_history')} variant="outline" size="sm" className="bg-blue-100 text-blue-700 border-blue-400 hover:bg-blue-200" data-testid="export-payment-btn">
              <Download className="h-3 w-3 mr-1" /> Payment Report
            </Button>
            <Button onClick={() => exportReportsToExcel('due')} variant="outline" size="sm" className="bg-red-100 text-red-700 border-red-400 hover:bg-red-200" data-testid="export-due-btn">
              <Download className="h-3 w-3 mr-1" /> Due Fees Report
            </Button>
            <Button onClick={() => exportReportsToExcel('collection')} variant="outline" size="sm" className="bg-amber-100 text-amber-700 border-amber-400 hover:bg-amber-200" data-testid="export-collection-btn">
              <Download className="h-3 w-3 mr-1" /> Collection Summary
            </Button>
            {reportFeeHead && (
              <span className="text-xs text-green-600 ml-2">Filter: {reportFeeHead}</span>
            )}
          </div>

          {/* Month Summary Cards */}
          {monthSummary && (
            <div className="grid grid-cols-2 md:grid-cols-7 gap-4">
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-blue-600">₹{(monthSummary.total_billed || 0).toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">Total Billed</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-green-600">₹{(monthSummary.total_collected || 0).toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">Collected</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-red-600">₹{(monthSummary.total_pending || 0).toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">Pending</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-amber-600">₹{(monthSummary.total_fine_waived || 0).toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">Fine Waived</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-orange-600">₹{(monthSummary.total_concession || 0).toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">Concessions</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-purple-600">₹{(monthSummary.total_late_fine || 0).toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">Late Fine</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold">{monthSummary.collection_percentage || 0}%</p>
                  <p className="text-xs text-muted-foreground">Collection Rate</p>
                </CardContent>
              </Card>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            {/* Head-wise Collection */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Head-wise Collection</CardTitle>
                <CardDescription>Fee category breakdown for {reportMonth}</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <div className="max-h-64 overflow-y-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50 sticky top-0">
                      <tr>
                        <th className="p-2 text-left text-xs">Fee Head</th>
                        <th className="p-2 text-right text-xs">Billed</th>
                        <th className="p-2 text-right text-xs">Collected</th>
                        <th className="p-2 text-right text-xs">%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {headWiseReport?.fee_heads?.map(h => (
                        <tr key={h.fee_head_id} className="border-t">
                          <td className="p-2 text-sm">{h.fee_head_name}</td>
                          <td className="p-2 text-sm text-right">₹{(h.total_billed || 0).toLocaleString()}</td>
                          <td className="p-2 text-sm text-right text-green-600">₹{(h.total_collected || 0).toLocaleString()}</td>
                          <td className="p-2 text-sm text-right">
                            <Badge variant={(h.collection_percentage || 0) >= 80 ? 'default' : (h.collection_percentage || 0) >= 50 ? 'secondary' : 'destructive'}>
                              {h.collection_percentage || 0}%
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Class-wise Collection */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Class-wise Collection</CardTitle>
                <CardDescription>By class for {reportMonth}</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <div className="max-h-64 overflow-y-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50 sticky top-0">
                      <tr>
                        <th className="p-2 text-left text-xs">Class</th>
                        <th className="p-2 text-center text-xs">Students</th>
                        <th className="p-2 text-right text-xs">Collected</th>
                        <th className="p-2 text-right text-xs">Pending</th>
                      </tr>
                    </thead>
                    <tbody>
                      {classWiseReport?.classes?.map(c => (
                        <tr key={c.class_id} className="border-t">
                          <td className="p-2 text-sm font-medium">{c.class_name}</td>
                          <td className="p-2 text-sm text-center">{c.total_students}</td>
                          <td className="p-2 text-sm text-right text-green-600">₹{c.total_collected?.toLocaleString()}</td>
                          <td className="p-2 text-sm text-right text-red-600">₹{c.total_pending?.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Today Collection */}
          <Card>
            <CardHeader>
              <CardTitle>Today Collection Report</CardTitle>
              <CardDescription>{dailyReport?.date} • Total: ₹{dailyReport?.total_collection?.toLocaleString() || 0}</CardDescription>
            </CardHeader>
            <CardContent>
              {/* By Payment Mode */}
              <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-6">
                {Object.entries(dailyReport?.by_payment_mode || {}).map(([mode, amount]) => (
                  <div key={mode} className="p-3 border rounded-lg text-center">
                    <p className="text-lg font-bold">₹{amount.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground capitalize">{mode.replace('_', ' ')}</p>
                  </div>
                ))}
              </div>

              {/* Recent Transactions */}
              <div>
                <h4 className="font-medium mb-3">Recent Transactions</h4>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="p-2 text-left text-xs">Receipt</th>
                        <th className="p-2 text-left text-xs">Student</th>
                        <th className="p-2 text-left text-xs">Class</th>
                        <th className="p-2 text-left text-xs">Mode</th>
                        <th className="p-2 text-right text-xs">Amount</th>
                        <th className="p-2 text-left text-xs">Time</th>
                        <th className="p-2 text-center text-xs">Print</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dailyReport?.payments?.slice(0, 10).map(p => (
                        <tr key={p.id} className="border-t hover:bg-muted/30">
                          <td className="p-2 text-sm font-mono">{p.receipt_number}</td>
                          <td className="p-2 text-sm">{p.student_name}</td>
                          <td className="p-2 text-sm">{p.class_name}</td>
                          <td className="p-2"><Badge variant="outline" className="text-xs">{p.payment_mode}</Badge></td>
                          <td className="p-2 text-sm text-right font-medium">₹{p.amount_paid?.toLocaleString()}</td>
                          <td className="p-2 text-sm text-muted-foreground">{p.payment_time}</td>
                          <td className="p-2">
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => downloadReceiptPDF(p.id, p.receipt_number)}
                              data-testid={`print-receipt-${p.id}`}
                            >
                              <Printer className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Discount Report */}
          <Card data-testid="discount-report-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Tag className="h-5 w-5 text-purple-500" />
                Discount / Concession Report
              </CardTitle>
              <CardDescription>
                {discountReport ? `${discountReport.payments_with_discount || discountReport.invoices_with_discount || 0} payments with discounts • Total: ₹${discountReport.total_discount_given?.toLocaleString() || 0}` : 'Loading...'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {discountReport?.discount_breakdown?.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No discounts applied in this period</p>
              ) : (
                <div className="space-y-4">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg text-center">
                      <p className="text-2xl font-bold text-purple-600">{discountReport?.total_discounts_configured || 0}</p>
                      <p className="text-xs text-muted-foreground">Configured</p>
                    </div>
                    <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg text-center">
                      <p className="text-2xl font-bold text-green-600">{discountReport?.discounts_in_use || 0}</p>
                      <p className="text-xs text-muted-foreground">In Use</p>
                    </div>
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-center">
                      <p className="text-2xl font-bold text-blue-600">₹{discountReport?.total_discount_given?.toLocaleString() || 0}</p>
                      <p className="text-xs text-muted-foreground">Total Given</p>
                    </div>
                  </div>

                  {/* Discount Breakdown */}
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="p-2 text-left text-xs">Discount Name</th>
                          <th className="p-2 text-left text-xs">Code</th>
                          <th className="p-2 text-center text-xs">Type</th>
                          <th className="p-2 text-center text-xs">Value</th>
                          <th className="p-2 text-center text-xs">Used Count</th>
                          <th className="p-2 text-right text-xs">Total Given</th>
                        </tr>
                      </thead>
                      <tbody>
                        {discountReport?.discount_breakdown?.map(d => (
                          <tr key={d.discount_id} className="border-t">
                            <td className="p-2 text-sm font-medium">{d.discount_name}</td>
                            <td className="p-2 text-sm text-muted-foreground">{d.discount_code}</td>
                            <td className="p-2 text-center">
                              <Badge variant="outline" className="text-xs">
                                {d.discount_type === 'percentage' ? '%' : '₹'}
                              </Badge>
                            </td>
                            <td className="p-2 text-center text-sm">
                              {d.discount_type === 'percentage' ? `${d.discount_value}%` : `₹${d.discount_value}`}
                            </td>
                            <td className="p-2 text-center text-sm font-medium">{d.usage_count}</td>
                            <td className="p-2 text-right text-sm font-bold text-green-600">₹{d.total_discount_amount?.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Fine Waiver Report */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="text-amber-500">💰</span>
                Fine Waiver Report
              </CardTitle>
              <CardDescription>
                {fineWaiverReport ? `${fineWaiverReport.summary?.total_waivers || 0} waivers • Total: ₹${fineWaiverReport.summary?.total_fine_waived?.toLocaleString() || 0}` : 'Loading...'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {fineWaiverReport?.summary?.total_waivers === 0 ? (
                <p className="text-center text-muted-foreground py-8">No fine waivers for selected month</p>
              ) : (
                <div className="space-y-4">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-4 gap-4">
                    <div className="p-4 bg-amber-50 rounded-lg text-center">
                      <p className="text-sm text-amber-600">Total Waivers</p>
                      <p className="text-2xl font-bold text-amber-700">{fineWaiverReport?.summary?.total_waivers || 0}</p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg text-center">
                      <p className="text-sm text-green-600">Full Waive</p>
                      <p className="text-2xl font-bold text-green-700">{fineWaiverReport?.summary?.full_waive_count || 0}</p>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg text-center">
                      <p className="text-sm text-blue-600">Partial Discount</p>
                      <p className="text-2xl font-bold text-blue-700">{fineWaiverReport?.summary?.partial_discount_count || 0}</p>
                    </div>
                    <div className="p-4 bg-red-50 rounded-lg text-center">
                      <p className="text-sm text-red-600">Total Amount Waived</p>
                      <p className="text-2xl font-bold text-red-700">₹{fineWaiverReport?.summary?.total_fine_waived?.toLocaleString() || 0}</p>
                    </div>
                  </div>
                  
                  {/* Waiver Records Table */}
                  {fineWaiverReport?.records?.length > 0 && (
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-amber-50">
                          <tr>
                            <th className="p-2 text-left text-sm">Date</th>
                            <th className="p-2 text-left text-sm">Receipt #</th>
                            <th className="p-2 text-left text-sm">Student</th>
                            <th className="p-2 text-left text-sm">Class</th>
                            <th className="p-2 text-center text-sm">Type</th>
                            <th className="p-2 text-right text-sm">Fine Waived</th>
                            <th className="p-2 text-right text-sm">Amount Paid</th>
                            <th className="p-2 text-left text-sm">Approved By</th>
                          </tr>
                        </thead>
                        <tbody>
                          {fineWaiverReport?.records?.map(r => (
                            <tr key={r.payment_id} className="border-t hover:bg-muted/50">
                              <td className="p-2 text-sm">{r.payment_date}</td>
                              <td className="p-2 text-sm font-mono">{r.receipt_number}</td>
                              <td className="p-2">
                                <p className="font-medium text-sm">{r.student_name}</p>
                                <p className="text-xs text-muted-foreground">{r.student_roll}</p>
                              </td>
                              <td className="p-2 text-sm">{r.class_name}</td>
                              <td className="p-2 text-center">
                                <Badge variant={r.discount_type === 'full_waive' ? 'destructive' : 'secondary'} className="text-xs">
                                  {r.discount_type_display}
                                </Badge>
                              </td>
                              <td className="p-2 text-right font-semibold text-amber-600">₹{r.fine_waived?.toLocaleString()}</td>
                              <td className="p-2 text-right text-sm">₹{r.total_amount_paid?.toLocaleString()}</td>
                              <td className="p-2 text-sm text-muted-foreground">{r.approved_by}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Defaulters Tab */}
        <TabsContent value="defaulters" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                Fee Defaulters ({defaulters?.total_defaulters || 0})
              </CardTitle>
              <CardDescription>
                Total Outstanding: ₹{defaulters?.total_outstanding?.toLocaleString() || 0}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="p-3 text-left text-sm">Student</th>
                      <th className="p-3 text-left text-sm">Class</th>
                      <th className="p-3 text-left text-sm">Father</th>
                      <th className="p-3 text-left text-sm">Phone</th>
                      <th className="p-3 text-center text-sm">Pending Months</th>
                      <th className="p-3 text-right text-sm">Total Due</th>
                      <th className="p-3 text-center text-sm">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {defaulters?.defaulters?.map(d => (
                      <tr key={d.student_id} className="border-t">
                        <td className="p-3">
                          <p className="font-medium">{d.student_name}</p>
                          <p className="text-xs text-muted-foreground">{d.student_roll}</p>
                        </td>
                        <td className="p-3 text-sm">{d.class_name}</td>
                        <td className="p-3 text-sm">{d.father_name}</td>
                        <td className="p-3 text-sm">{d.phone}</td>
                        <td className="p-3 text-center">
                          <Badge variant="destructive">{d.pending_months}</Badge>
                        </td>
                        <td className="p-3 text-right font-semibold text-red-600">
                          ₹{d.total_due?.toLocaleString()}
                        </td>
                        <td className="p-3 text-center">
                          <Button size="sm" variant="outline" onClick={() => selectStudent({ id: d.student_id, name: d.student_name, student_id: d.student_roll, class_name: d.class_name, father_name: d.father_name }, true)}>
                            Collect
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination for Defaulters */}
              {defaulters?.pagination && defaulters.pagination.total_pages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    Showing {((defaultersPage - 1) * defaultersPageSize) + 1} - {Math.min(defaultersPage * defaultersPageSize, defaulters.pagination.total)} of {defaulters.pagination.total} defaulters
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchDefaulters(defaultersPage - 1)}
                      disabled={!defaulters.pagination.has_prev}
                    >
                      Previous
                    </Button>
                    <span className="text-sm px-2">
                      Page {defaultersPage} of {defaulters.pagination.total_pages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchDefaulters(defaultersPage + 1)}
                      disabled={!defaulters.pagination.has_next}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Fee Reminders Tab */}
        <TabsContent value="reminders" className="space-y-4">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Reminder Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="h-5 w-5" /> Reminder Settings
                </CardTitle>
                <CardDescription>Configure automatic fee payment reminders for parents</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium">Enable Fee Reminders</p>
                    <p className="text-sm text-muted-foreground">Automatically notify parents about pending fees</p>
                  </div>
                  <Switch
                    checked={reminderSettings.enabled}
                    onCheckedChange={v => setReminderSettings(p => ({ ...p, enabled: v }))}
                  />
                </div>

                <div>
                  <Label>Days Before Due Date</Label>
                  <p className="text-xs text-muted-foreground mb-2">Send reminder this many days before fee is due</p>
                  <Select 
                    value={String(reminderSettings.days_before_due)} 
                    onValueChange={v => setReminderSettings(p => ({ ...p, days_before_due: parseInt(v) }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3">3 days</SelectItem>
                      <SelectItem value="5">5 days</SelectItem>
                      <SelectItem value="7">7 days</SelectItem>
                      <SelectItem value="10">10 days</SelectItem>
                      <SelectItem value="15">15 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={reminderSettings.send_on_due_date}
                    onCheckedChange={v => setReminderSettings(p => ({ ...p, send_on_due_date: v }))}
                  />
                  <Label>Send reminder on due date</Label>
                </div>

                <div>
                  <Label>Reminder After Due (for overdue)</Label>
                  <p className="text-xs text-muted-foreground mb-2">Send reminders on these days after due date</p>
                  <div className="flex flex-wrap gap-2">
                    {[1, 3, 5, 7, 10, 15, 30].map(day => (
                      <Badge
                        key={day}
                        variant={reminderSettings.days_after_due?.includes(day) ? 'default' : 'outline'}
                        className="cursor-pointer"
                        onClick={() => {
                          const current = reminderSettings.days_after_due || [];
                          const updated = current.includes(day) 
                            ? current.filter(d => d !== day)
                            : [...current, day].sort((a, b) => a - b);
                          setReminderSettings(p => ({ ...p, days_after_due: updated }));
                        }}
                      >
                        Day {day}
                      </Badge>
                    ))}
                  </div>
                </div>

                <Button onClick={saveReminderSettings} className="w-full">
                  Save Settings
                </Button>
              </CardContent>
            </Card>

            {/* Send Reminders Now */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Send className="h-5 w-5" /> Send Reminders Now
                </CardTitle>
                <CardDescription>Manually trigger fee reminders based on current settings</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-muted rounded-lg space-y-2">
                  <p className="text-sm">This will send reminders for:</p>
                  <ul className="text-sm space-y-1">
                    <li>• Fees due in {reminderSettings.days_before_due} days</li>
                    {reminderSettings.send_on_due_date && <li>• Fees due today</li>}
                    <li>• Overdue fees ({reminderSettings.days_after_due?.join(', ')} days old)</li>
                  </ul>
                </div>

                {!reminderSettings.enabled && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-700">
                      ⚠️ Reminders are currently disabled. Enable them in settings first.
                    </p>
                  </div>
                )}

                <Button 
                  onClick={sendFeeReminders} 
                  disabled={!reminderSettings.enabled || sendingReminders}
                  className="w-full"
                  variant={reminderSettings.enabled ? 'default' : 'secondary'}
                >
                  {sendingReminders ? 'Sending...' : 'Send Fee Reminders Now'}
                </Button>

                <div className="text-xs text-muted-foreground">
                  <p>💡 Reminders are sent as in-app notifications. Parents can see them in their dashboard.</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Collection Dialog - Compact */}
      <Dialog open={collectionDialog} onOpenChange={setCollectionDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Collect Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {/* Student Info - Compact */}
            <div className="p-2 bg-muted rounded-lg flex justify-between items-center">
              <div>
                <p className="font-medium text-sm">{selectedStudent?.name}</p>
                <p className="text-xs text-muted-foreground">{selectedStudent?.student_id}</p>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold">₹{calculateTotal().toLocaleString()}</p>
                {pendingInvoices.filter(inv => selectedInvoices.includes(inv.id)).reduce((sum, inv) => sum + (inv.late_fine || 0), 0) > 0 && (
                  <p className="text-xs text-amber-600">
                    (+₹{pendingInvoices.filter(inv => selectedInvoices.includes(inv.id)).reduce((sum, inv) => sum + (inv.late_fine || 0), 0)} fine)
                  </p>
                )}
              </div>
            </div>

            {/* Late Fine Options - Compact inline */}
            {pendingInvoices.filter(inv => selectedInvoices.includes(inv.id)).reduce((sum, inv) => sum + (inv.late_fine || 0), 0) > 0 && (
              <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-medium text-amber-800">Late Fine:</span>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={paymentForm.waive_fine}
                        onChange={e => {
                          const totalFine = pendingInvoices.filter(inv => selectedInvoices.includes(inv.id)).reduce((sum, inv) => sum + (inv.late_fine || 0), 0);
                          setPaymentForm(p => ({ ...p, waive_fine: e.target.checked, fine_discount: 0 }));
                        }}
                        className="w-3 h-3 text-amber-600 rounded"
                      />
                      <span className="text-xs text-amber-800">Full Waive</span>
                    </label>
                    {!paymentForm.waive_fine && (
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-amber-700">Or ₹</span>
                        <input
                          type="number"
                          min="0"
                          max={pendingInvoices.filter(inv => selectedInvoices.includes(inv.id)).reduce((sum, inv) => sum + (inv.late_fine || 0), 0)}
                          value={paymentForm.fine_discount || ''}
                          onChange={e => setPaymentForm(p => ({ ...p, fine_discount: parseFloat(e.target.value) || 0 }))}
                          className="w-16 px-1 py-0.5 text-xs border border-amber-300 rounded"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Concession - Compact */}
            <div className="p-2 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-green-800">Concession:</span>
                <Select
                  value={paymentForm.applied_discount_id || 'none'}
                  onValueChange={v => {
                    if (v === 'none') {
                      setPaymentForm(p => ({ ...p, applied_discount_id: '', applied_discount_amount: 0 }));
                    } else if (v === 'custom') {
                      setPaymentForm(p => ({ ...p, applied_discount_id: 'custom' }));
                    } else {
                      const selectedDiscount = discounts.find(d => d.id === v);
                      if (selectedDiscount) {
                        const baseTotal = calculateTotal() - pendingInvoices.filter(inv => selectedInvoices.includes(inv.id)).reduce((sum, inv) => sum + (inv.late_fine || 0), 0);
                        let discountAmt = 0;
                        if (selectedDiscount.discount_type === 'percentage') {
                          discountAmt = Math.round(baseTotal * (selectedDiscount.discount_value || 0) / 100);
                        } else {
                          discountAmt = selectedDiscount.discount_value || 0;
                        }
                        setPaymentForm(p => ({ ...p, applied_discount_id: v, applied_discount_amount: discountAmt }));
                      }
                    }
                  }}
                >
                  <SelectTrigger className="h-7 text-xs flex-1">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {discounts.map(d => (
                      <SelectItem key={d.id} value={d.id}>{d.name} ({d.discount_type === 'percentage' ? `${d.discount_value}%` : `₹${d.discount_value}`})</SelectItem>
                    ))}
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
                {paymentForm.applied_discount_id === 'custom' && (
                  <input
                    type="number"
                    min="0"
                    value={paymentForm.applied_discount_amount || ''}
                    onChange={e => setPaymentForm(p => ({ ...p, applied_discount_amount: parseFloat(e.target.value) || 0 }))}
                    placeholder="₹"
                    className="w-16 px-1 py-0.5 text-xs border border-green-300 rounded"
                  />
                )}
              </div>
            </div>

            {/* Payment Mode & Amount - Two columns */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Payment Mode *</Label>
                <Select value={paymentForm.payment_mode} onValueChange={v => setPaymentForm(p => ({ ...p, payment_mode: v }))}>
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="card">Card</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Amount *</Label>
                <Input
                  type="number"
                  value={paymentForm.amount_paid > 0 ? paymentForm.amount_paid : (calculateTotal() - (paymentForm.applied_discount_amount || 0) - (paymentForm.waive_fine ? pendingInvoices.filter(inv => selectedInvoices.includes(inv.id)).reduce((sum, inv) => sum + (inv.late_fine || 0), 0) : (paymentForm.fine_discount || 0)))}
                  onChange={e => setPaymentForm(p => ({ ...p, amount_paid: parseFloat(e.target.value) || 0 }))}
                  min="1"
                  className="h-8 text-sm font-medium"
                />
              </div>
            </div>

            {/* Remarks - Smaller */}
            <div>
              <Label className="text-xs">Remarks</Label>
              <Input
                value={paymentForm.remarks || ''}
                onChange={e => setPaymentForm(p => ({ ...p, remarks: e.target.value }))}
                placeholder="Optional"
                className="h-8 text-sm"
              />
            </div>
          </div>
          
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setCollectionDialog(false)} size="sm" disabled={isCollectingPayment}>Cancel</Button>
            <Button onClick={collectPayment} size="sm" disabled={isCollectingPayment} data-testid="collect-payment-btn">
              {isCollectingPayment ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Collect ₹{(paymentForm.amount_paid > 0 ? paymentForm.amount_paid : (calculateTotal() - (paymentForm.applied_discount_amount || 0) - (paymentForm.waive_fine ? pendingInvoices.filter(inv => selectedInvoices.includes(inv.id)).reduce((sum, inv) => sum + (inv.late_fine || 0), 0) : (paymentForm.fine_discount || 0)))).toLocaleString()}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receipt Dialog */}
      <Dialog open={receiptDialog} onOpenChange={setReceiptDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Payment Receipt</DialogTitle>
          </DialogHeader>
          {receiptData && (
            <div className="space-y-4 text-sm" id="receipt-print">
              <div className="text-center border-b pb-3">
                <h2 className="font-bold text-lg">{receiptData.school?.name}</h2>
                <p className="text-muted-foreground">{receiptData.school?.address}</p>
              </div>
              
              <div className="flex justify-between">
                <span>Receipt No:</span>
                <span className="font-mono font-medium">{receiptData.receipt_number}</span>
              </div>
              <div className="flex justify-between">
                <span>Date:</span>
                <span>{receiptData.payment_date} {receiptData.payment_time}</span>
              </div>
              
              <div className="border-t border-b py-3">
                <p><strong>Student:</strong> {receiptData.student?.name}</p>
                <p><strong>ID:</strong> {receiptData.student?.student_id}</p>
                <p><strong>Class:</strong> {receiptData.student?.class_name}</p>
                <p><strong>Father:</strong> {receiptData.student?.father_name}</p>
              </div>
              
              <div>
                <p className="font-medium mb-2">Particulars:</p>
                {receiptData.invoices?.map((inv, i) => (
                  <div key={i} className="flex justify-between">
                    <span>{inv.fee_head} ({inv.month})</span>
                    <span>₹{inv.amount?.toLocaleString()}</span>
                  </div>
                ))}
              </div>
              
              <div className="border-t pt-3 font-bold flex justify-between text-lg">
                <span>Total Paid:</span>
                <span>₹{receiptData.amount_paid?.toLocaleString()}</span>
              </div>
              
              <div className="text-muted-foreground">
                <p>Payment Mode: {receiptData.payment_mode}</p>
                {receiptData.transaction_id && <p>Transaction ID: {receiptData.transaction_id}</p>}
                <p>Received By: {receiptData.collected_by}</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReceiptDialog(false)}>Close</Button>
            <Button onClick={printReceipt}>
              <Printer className="h-4 w-4 mr-2" /> Print Receipt
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fee Head Dialog */}
      <Dialog open={feeHeadDialog} onOpenChange={(open) => { setFeeHeadDialog(open); if (!open) setEditingFeeHead(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingFeeHead ? 'Edit Fee Head' : 'Add Fee Head'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input 
                  value={feeHeadForm.name} 
                  onChange={e => {
                    const name = e.target.value;
                    // Auto-generate code from name (first letters of each word)
                    const code = name
                      .split(' ')
                      .filter(word => word.length > 0)
                      .map(word => word.charAt(0))
                      .join('')
                      .toUpperCase()
                      .slice(0, 5);
                    setFeeHeadForm(p => ({ ...p, name, code: code || p.code }));
                  }} 
                  placeholder="e.g., Tuition Fee" 
                />
              </div>
              <div>
                <Label>Code * (Auto-generated)</Label>
                <Input 
                  value={feeHeadForm.code} 
                  onChange={e => setFeeHeadForm(p => ({ ...p, code: e.target.value.toUpperCase() }))} 
                  placeholder="e.g., TF"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Fee Type</Label>
                <Select value={feeHeadForm.fee_type} onValueChange={v => setFeeHeadForm(p => ({ ...p, fee_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FEE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Payment Schedule</Label>
                <Select 
                  value={feeHeadForm.fee_category} 
                  onValueChange={v => {
                    const category = FEE_CATEGORIES.find(c => c.value === v);
                    setFeeHeadForm(p => ({ 
                      ...p, 
                      fee_category: v,
                      is_recurring: category?.isRecurring || false,
                      frequency: category?.frequency || 'monthly'
                    }));
                  }}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FEE_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  {feeHeadForm.fee_category === 'monthly' || feeHeadForm.fee_category === 'quarterly' 
                    ? '✓ System will auto-generate invoices' 
                    : 'Manual invoice creation'}
                </p>
              </div>
            </div>
            
            <div className="border rounded-lg p-3 space-y-2">
              <Label className="text-sm font-medium">Apply to Admission Type:</Label>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <Checkbox checked={feeHeadForm.apply_on_new_admission} onCheckedChange={v => setFeeHeadForm(p => ({ ...p, apply_on_new_admission: v }))} />
                  <Label className="text-sm">New Admission</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox checked={feeHeadForm.apply_on_re_admission} onCheckedChange={v => setFeeHeadForm(p => ({ ...p, apply_on_re_admission: v }))} />
                  <Label className="text-sm">Re-Admission</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox checked={feeHeadForm.apply_on_promotion} onCheckedChange={v => setFeeHeadForm(p => ({ ...p, apply_on_promotion: v }))} />
                  <Label className="text-sm font-medium text-green-600">Promotion</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox checked={feeHeadForm.apply_on_transfer} onCheckedChange={v => setFeeHeadForm(p => ({ ...p, apply_on_transfer: v }))} />
                  <Label className="text-sm">Transfer</Label>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setFeeHeadDialog(false); setEditingFeeHead(null); }}>Cancel</Button>
            <Button onClick={createFeeHead}>{editingFeeHead ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Structure Dialog */}
      <Dialog open={structureDialog} onOpenChange={(open) => { setStructureDialog(open); if (!open) setEditingStructure(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingStructure ? 'Edit Fee Structure' : 'Add Fee Structure'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Academic Year *</Label>
                <Select value={structureForm.academic_year} onValueChange={v => setStructureForm(p => ({ ...p, academic_year: v }))}>
                  <SelectTrigger><SelectValue placeholder="Select academic year" /></SelectTrigger>
                  <SelectContent>
                    {academicSessions.map(s => (
                      <SelectItem key={s.id} value={s.session_name}>
                        {s.session_name} {s.is_current && '(Current)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Class *</Label>
                <Select value={structureForm.class_id} onValueChange={v => setStructureForm(p => ({ ...p, class_id: v }))} disabled={!!editingStructure}>
                  <SelectTrigger><SelectValue placeholder="Select class" /></SelectTrigger>
                  <SelectContent>
                    {classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Fee Head *</Label>
              <Select value={structureForm.fee_head_id} onValueChange={v => setStructureForm(p => ({ ...p, fee_head_id: v }))} disabled={!!editingStructure}>
                <SelectTrigger><SelectValue placeholder="Select fee head" /></SelectTrigger>
                <SelectContent>
                  {feeHeads.map(h => (
                    <SelectItem key={h.id} value={h.id}>
                      {h.name} <span className="text-muted-foreground text-xs ml-1">({h.fee_category})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {structureForm.fee_head_id && (
                <p className="text-xs text-muted-foreground mt-1">
                  Category: {feeHeads.find(h => h.id === structureForm.fee_head_id)?.fee_category || 'N/A'}
                  {feeHeads.find(h => h.id === structureForm.fee_head_id)?.fee_category === 'one_time' && 
                    ' - Due Date/Fine not applicable for one-time fees'}
                </p>
              )}
            </div>
            {(() => {
              const selectedFeeHead = feeHeads.find(h => h.id === structureForm.fee_head_id);
              const isOneTime = selectedFeeHead?.fee_category === 'one_time';
              const isRecurring = selectedFeeHead?.fee_category === 'monthly' || selectedFeeHead?.fee_category === 'quarterly';
              
              return (
                <div className={`grid gap-4 ${isOneTime ? 'grid-cols-1' : 'grid-cols-3'}`}>
                  <div>
                    <Label>Amount (₹) *</Label>
                    <Input type="number" value={structureForm.amount} onChange={e => setStructureForm(p => ({ ...p, amount: e.target.value }))} />
                  </div>
                  {!isOneTime && (
                    <>
                      <div>
                        <Label>Due Day {isRecurring && <span className="text-destructive">*</span>}</Label>
                        <Input 
                          type="number" 
                          min="1" 
                          max="28" 
                          value={structureForm.due_day} 
                          onChange={e => setStructureForm(p => ({ ...p, due_day: parseInt(e.target.value) }))} 
                          placeholder="e.g., 10"
                        />
                        <p className="text-xs text-muted-foreground mt-1">Day of month (1-28)</p>
                      </div>
                      
                      {/* Late Fine Type Selection */}
                      <div className="col-span-2">
                        <Label>Late Fine Type</Label>
                        <select
                          value={structureForm.late_fine_type || 'per_day'}
                          onChange={e => setStructureForm(p => ({ ...p, late_fine_type: e.target.value }))}
                          className="w-full p-2 border rounded-md"
                        >
                          <option value="per_day">Per Day (Daily Fine)</option>
                          <option value="monthly">Monthly (Flat per Month)</option>
                          <option value="flat">One-time Flat Fine</option>
                        </select>
                      </div>
                      
                      {/* Conditional Fine Amount Fields */}
                      {structureForm.late_fine_type === 'per_day' && (
                        <div>
                          <Label>Late Fine/Day (₹)</Label>
                          <Input 
                            type="number" 
                            min="0"
                            value={structureForm.late_fine_per_day} 
                            onChange={e => setStructureForm(p => ({ ...p, late_fine_per_day: e.target.value }))} 
                            placeholder="0"
                          />
                        </div>
                      )}
                      
                      {structureForm.late_fine_type === 'monthly' && (
                        <div>
                          <Label>Monthly Fine (₹/month)</Label>
                          <Input 
                            type="number" 
                            min="0"
                            value={structureForm.late_fine_monthly} 
                            onChange={e => setStructureForm(p => ({ ...p, late_fine_monthly: e.target.value }))} 
                            placeholder="0"
                          />
                          <p className="text-xs text-muted-foreground mt-1">Flat fine per month late</p>
                        </div>
                      )}
                      
                      {structureForm.late_fine_type === 'flat' && (
                        <div>
                          <Label>Flat Fine Amount (₹)</Label>
                          <Input 
                            type="number" 
                            min="0"
                            value={structureForm.late_fine_monthly} 
                            onChange={e => setStructureForm(p => ({ ...p, late_fine_monthly: e.target.value }))} 
                            placeholder="0"
                          />
                          <p className="text-xs text-muted-foreground mt-1">One-time fine if overdue</p>
                        </div>
                      )}
                      
                      <div>
                        <Label>Max Fine Cap (₹)</Label>
                        <Input 
                          type="number" 
                          min="0"
                          value={structureForm.max_late_fine} 
                          onChange={e => setStructureForm(p => ({ ...p, max_late_fine: e.target.value }))} 
                          placeholder="0 (no cap)"
                        />
                        <p className="text-xs text-muted-foreground mt-1">Maximum fine limit (0 = no cap)</p>
                      </div>
                    </>
                  )}
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setStructureDialog(false); setEditingStructure(null); }}>Cancel</Button>
            <Button onClick={createStructure}>{editingStructure ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Discount Dialog */}
      <Dialog open={discountDialog} onOpenChange={(open) => {
        setDiscountDialog(open);
        if (!open) {
          setEditingDiscount(null);
          setDiscountForm({ name: '', code: '', discount_type: 'percentage', discount_value: '', applicable_fee_heads: [], description: '' });
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingDiscount ? 'Edit Discount' : 'Add Discount / Concession'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input 
                  value={discountForm.name} 
                  onChange={e => setDiscountForm(p => ({ ...p, name: e.target.value }))} 
                  placeholder="e.g., Sibling Discount"
                  data-testid="discount-name-input"
                />
              </div>
              <div>
                <Label>Code {editingDiscount ? '(read-only)' : '*'}</Label>
                <Input 
                  value={discountForm.code} 
                  onChange={e => setDiscountForm(p => ({ ...p, code: e.target.value.toUpperCase() }))} 
                  placeholder="e.g., SIB"
                  data-testid="discount-code-input"
                  disabled={!!editingDiscount}
                  className={editingDiscount ? 'bg-gray-100' : ''}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Discount Type</Label>
                <Select value={discountForm.discount_type} onValueChange={v => setDiscountForm(p => ({ ...p, discount_type: v }))}>
                  <SelectTrigger data-testid="discount-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                    <SelectItem value="fixed">Fixed Amount (₹)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Value *</Label>
                <Input 
                  type="number" 
                  value={discountForm.discount_value} 
                  onChange={e => setDiscountForm(p => ({ ...p, discount_value: e.target.value }))}
                  placeholder={discountForm.discount_type === 'percentage' ? 'e.g., 10' : 'e.g., 500'}
                  data-testid="discount-value-input"
                />
              </div>
            </div>
            <div>
              <Label>Applicable Fee Heads (Optional)</Label>
              <p className="text-xs text-muted-foreground mb-2">Leave empty to apply to all fee heads</p>
              <div className="flex flex-wrap gap-2 border rounded-lg p-2 max-h-32 overflow-y-auto">
                {feeHeads.map(h => (
                  <Badge 
                    key={h.id}
                    variant={discountForm.applicable_fee_heads.includes(h.id) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => {
                      const current = discountForm.applicable_fee_heads || [];
                      const updated = current.includes(h.id) 
                        ? current.filter(id => id !== h.id)
                        : [...current, h.id];
                      setDiscountForm(p => ({ ...p, applicable_fee_heads: updated }));
                    }}
                  >
                    {h.name}
                  </Badge>
                ))}
              </div>
            </div>
            <div>
              <Label>Description (Optional)</Label>
              <Textarea 
                value={discountForm.description} 
                onChange={e => setDiscountForm(p => ({ ...p, description: e.target.value }))}
                placeholder="Additional notes about this discount"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setDiscountDialog(false);
              setEditingDiscount(null);
              setDiscountForm({ name: '', code: '', discount_type: 'percentage', discount_value: '', applicable_fee_heads: [], description: '' });
            }}>Cancel</Button>
            {editingDiscount ? (
              <Button onClick={updateDiscount} data-testid="update-discount-btn">Update Discount</Button>
            ) : (
              <Button onClick={createDiscount} data-testid="create-discount-btn">Create Discount</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Student Fee Category Dialog */}
      <Dialog open={feeCategoryDialog} onOpenChange={(open) => {
        setFeeCategoryDialog(open);
        if (!open) {
          setEditingCategory(null);
          setFeeCategoryForm({ name: '', code: '', discount_percentage: 0, description: '' });
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingCategory ? 'Edit Fee Category' : 'Add Student Fee Category'}</DialogTitle>
            <p className="text-sm text-muted-foreground">
              {editingCategory ? 'Update category details and discount percentage' : 'Create categories for student admission with auto-discount'}
            </p>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input 
                  value={feeCategoryForm.name} 
                  onChange={e => setFeeCategoryForm(p => ({ ...p, name: e.target.value }))} 
                  placeholder="e.g., Staff Ward"
                  data-testid="fee-category-name-input"
                />
              </div>
              <div>
                <Label>Code {editingCategory ? '(read-only)' : '*'}</Label>
                <Input 
                  value={feeCategoryForm.code} 
                  onChange={e => setFeeCategoryForm(p => ({ ...p, code: e.target.value.toLowerCase().replace(/\s+/g, '_') }))} 
                  placeholder="e.g., staff_ward"
                  data-testid="fee-category-code-input"
                  disabled={!!editingCategory}
                  className={editingCategory ? 'bg-gray-100' : ''}
                />
              </div>
            </div>
            <div>
              <Label>Discount Percentage</Label>
              <Input 
                type="number" 
                min="0"
                max="100"
                value={feeCategoryForm.discount_percentage} 
                onChange={e => setFeeCategoryForm(p => ({ ...p, discount_percentage: parseFloat(e.target.value) || 0 }))}
                placeholder="e.g., 25"
                data-testid="fee-category-discount-input"
              />
              <p className="text-xs text-muted-foreground mt-1">This discount will be auto-applied when this category is selected during admission</p>
            </div>
            <div>
              <Label>Description</Label>
              <Input 
                value={feeCategoryForm.description} 
                onChange={e => setFeeCategoryForm(p => ({ ...p, description: e.target.value }))}
                placeholder="e.g., 25% discount for staff children"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setFeeCategoryDialog(false);
              setEditingCategory(null);
              setFeeCategoryForm({ name: '', code: '', discount_percentage: 0, description: '' });
            }}>Cancel</Button>
            {editingCategory ? (
              <Button onClick={updateFeeCategory} data-testid="update-fee-category-btn">Update Category</Button>
            ) : (
              <Button onClick={createFeeCategory} data-testid="create-fee-category-btn">Create Category</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate Invoice Dialog */}
      <Dialog open={generateInvoiceDialog} onOpenChange={setGenerateInvoiceDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Generate Monthly Invoices</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              This will generate fee invoices for all students with recurring monthly fees for the selected month.
            </p>
            <div>
              <Label>Month</Label>
              <Input 
                type="month" 
                value={generateMonth} 
                onChange={e => setGenerateMonth(e.target.value)} 
              />
            </div>
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
              <p className="font-medium text-amber-800">Note:</p>
              <ul className="list-disc list-inside text-amber-700 mt-1">
                <li>Only recurring monthly fees will be invoiced</li>
                <li>Existing invoices for this month will be skipped</li>
                <li>Late fines will be applied if past due date</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenerateInvoiceDialog(false)}>Cancel</Button>
            <Button onClick={generateMonthlyInvoices} disabled={generating}>
              {generating ? 'Generating...' : 'Generate Invoices'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fee Templates Dialog */}
      <Dialog open={templateDialog} onOpenChange={setTemplateDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Fee Head Templates</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Select predefined fee templates to quickly setup your fee structure. Templates include admission fees, 
              tuition fees, transport, hostel, and more with proper categorization.
            </p>
            
            <div className="flex justify-between items-center">
              <p className="text-sm font-medium">{selectedTemplates.length} selected</p>
              <Button variant="outline" size="sm" onClick={setupAllTemplates} disabled={loadingTemplates}>
                <Zap className="h-4 w-4 mr-1" /> Setup All Templates
              </Button>
            </div>
            
            <div className="grid md:grid-cols-2 gap-3">
              {feeTemplates.map(t => (
                <div 
                  key={t.code}
                  onClick={() => toggleTemplateSelection(t.code)}
                  className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                    selectedTemplates.includes(t.code) ? 'bg-primary/10 border-primary' : 'hover:bg-muted'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <Checkbox checked={selectedTemplates.includes(t.code)} />
                      <div>
                        <p className="font-medium text-sm">{t.name}</p>
                        <p className="text-xs text-muted-foreground font-mono">{t.code}</p>
                      </div>
                    </div>
                    <Badge variant="outline" className="text-xs capitalize">
                      {(t.fee_category || 'other').replace('_', ' ')}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">{t.description}</p>
                  <div className="flex gap-1 mt-2 flex-wrap">
                    {t.apply_on_new_admission && <Badge variant="secondary" className="text-[10px]">New</Badge>}
                    {t.apply_on_re_admission && <Badge variant="secondary" className="text-[10px]">Re-Adm</Badge>}
                    {t.apply_on_promotion && <Badge variant="outline" className="text-[10px] border-green-500 text-green-600">Promo</Badge>}
                    {t.apply_on_transfer && <Badge variant="secondary" className="text-[10px]">Transfer</Badge>}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setTemplateDialog(false); setSelectedTemplates([]); }}>Cancel</Button>
            <Button onClick={setupFromTemplates} disabled={selectedTemplates.length === 0 || loadingTemplates}>
              {loadingTemplates ? 'Creating...' : `Create ${selectedTemplates.length} Fee Heads`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admission Receipt Details Dialog */}
      <Dialog open={admissionReceiptDialog} onOpenChange={setAdmissionReceiptDialog}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Admission Receipt</DialogTitle>
          </DialogHeader>
          {admissionReceiptData && (
            <div className="space-y-4 text-sm">
              <div className="text-center border-b pb-3">
                <h2 className="font-bold text-lg">{admissionReceiptData.school?.name}</h2>
                <p className="text-muted-foreground">{admissionReceiptData.school?.address}</p>
              </div>
              
              <div className="flex justify-between text-xs">
                <span>Receipt No: <strong>{admissionReceiptData.receipt_number}</strong></span>
                <span>Date: {admissionReceiptData.date}</span>
              </div>
              
              <div className="border rounded-lg p-3 bg-muted/30">
                <p className="font-medium">{admissionReceiptData.student?.name}</p>
                <p className="text-xs text-muted-foreground">
                  Admission No: {admissionReceiptData.student?.admission_number} • Class: {admissionReceiptData.student?.class}
                </p>
                <p className="text-xs text-muted-foreground">
                  Father: {admissionReceiptData.student?.father_name} • Contact: {admissionReceiptData.student?.contact}
                </p>
                <Badge className="mt-2 capitalize">{admissionReceiptData.student?.admission_type || 'new'} Admission</Badge>
              </div>

              {admissionReceiptData.fees?.one_time?.length > 0 && (
                <div>
                  <p className="font-medium text-xs text-muted-foreground mb-2">ONE-TIME FEES</p>
                  {admissionReceiptData.fees.one_time.map((f, i) => (
                    <div key={i} className="flex justify-between py-1 border-b border-dashed">
                      <span>{f.fee_name}</span>
                      <div className="text-right">
                        <span className="font-medium">₹{f.amount?.toLocaleString()}</span>
                        <Badge variant={f.status === 'paid' ? 'default' : 'secondary'} className="ml-2 text-[10px]">{f.status}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {admissionReceiptData.fees?.annual?.length > 0 && (
                <div>
                  <p className="font-medium text-xs text-muted-foreground mb-2">ANNUAL FEES</p>
                  {admissionReceiptData.fees.annual.map((f, i) => (
                    <div key={i} className="flex justify-between py-1 border-b border-dashed">
                      <span>{f.fee_name}</span>
                      <div className="text-right">
                        <span className="font-medium">₹{f.amount?.toLocaleString()}</span>
                        <Badge variant={f.status === 'paid' ? 'default' : 'secondary'} className="ml-2 text-[10px]">{f.status}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {admissionReceiptData.fees?.recurring?.length > 0 && (
                <div>
                  <p className="font-medium text-xs text-muted-foreground mb-2">RECURRING FEES</p>
                  {admissionReceiptData.fees.recurring.map((f, i) => (
                    <div key={i} className="flex justify-between py-1 border-b border-dashed">
                      <span>{f.fee_name}</span>
                      <div className="text-right">
                        <span className="font-medium">₹{f.amount?.toLocaleString()}</span>
                        <Badge variant={f.status === 'paid' ? 'default' : 'secondary'} className="ml-2 text-[10px]">{f.status}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="border-t pt-3 space-y-1">
                <div className="flex justify-between font-medium">
                  <span>Total Amount:</span>
                  <span>₹{admissionReceiptData.summary?.total_amount?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-green-600">
                  <span>Total Paid:</span>
                  <span>₹{admissionReceiptData.summary?.total_paid?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-red-600 font-medium">
                  <span>Balance Due:</span>
                  <span>₹{admissionReceiptData.summary?.total_balance?.toLocaleString()}</span>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdmissionReceiptDialog(false)}>Close</Button>
            <Button onClick={() => window.print()}>
              <Printer className="h-4 w-4 mr-2" /> Print
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Full Year Invoice Generation Dialog */}
      <Dialog open={fullYearDialog} onOpenChange={setFullYearDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" /> Generate Full Year Tuition Invoices
            </DialogTitle>
            <DialogDescription>
              Generate all 12 months of tuition fee invoices with 5% advance payment discount.
            </DialogDescription>
          </DialogHeader>
          
          {fullYearStudent && (
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <p className="font-semibold">{fullYearStudent.name}</p>
                <p className="text-sm text-muted-foreground">Academic Year: {studentFeeDetails?.academic_year || currentSession?.session_name || 'N/A'}</p>
              </div>
              
              <div className="p-4 border rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span>Monthly Tuition Fee:</span>
                  <span>₹{studentFeeDetails?.payment_options?.pay_full_year?.original_amount?.toLocaleString() || 0} × 12 months</span>
                </div>
                <div className="flex justify-between">
                  <span>Original Total:</span>
                  <span>₹{((studentFeeDetails?.payment_options?.pay_full_year?.original_amount || 0) * 12).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-green-600">
                  <span>Discount (5%):</span>
                  <span>-₹{(((studentFeeDetails?.payment_options?.pay_full_year?.original_amount || 0) * 12) * 0.05).toLocaleString()}</span>
                </div>
                <div className="flex justify-between font-bold text-lg border-t pt-2">
                  <span>Final Amount:</span>
                  <span>₹{(((studentFeeDetails?.payment_options?.pay_full_year?.original_amount || 0) * 12) * 0.95).toLocaleString()}</span>
                </div>
              </div>
              
              {fullYearData && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="font-semibold text-green-800">✅ Invoices Generated!</p>
                  <p className="text-sm text-green-600">{fullYearData.message}</p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setFullYearDialog(false); setFullYearData(null); }}>
              Close
            </Button>
            {!fullYearData && (
              <Button onClick={generateFullYearInvoices} disabled={generatingFullYear}>
                {generatingFullYear ? 'Generating...' : 'Generate & Apply Discount'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Generation Confirmation Dialog */}
      <Dialog open={invoiceConfirmDialog} onOpenChange={setInvoiceConfirmDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Generate Monthly Invoices
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {invoiceCheckResult?.exists ? (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="font-semibold text-amber-800">⚠️ Warning: Invoices Already Exist!</p>
                <p className="text-sm text-amber-700 mt-2">
                  <strong>{invoiceCheckResult.count}</strong> invoices already generated for <strong>{generateMonth}</strong>
                </p>
                {invoiceCheckResult.last_generated && (
                  <p className="text-xs text-amber-600 mt-1">
                    Last generated: {new Date(invoiceCheckResult.last_generated).toLocaleString()}
                  </p>
                )}
                <p className="text-sm text-amber-700 mt-3">
                  This was likely generated by the Cron Job. Generating again may create duplicate invoices!
                </p>
              </div>
            ) : (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="font-semibold text-blue-800">ℹ️ No Existing Invoices</p>
                <p className="text-sm text-blue-700 mt-2">
                  No invoices found for <strong>{generateMonth}</strong>. It is safe to generate new invoices.
                </p>
              </div>
            )}
            
            <p className="text-sm text-muted-foreground">
              This action will generate monthly fee invoices for all active students.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInvoiceConfirmDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={generateMonthlyInvoices} 
              disabled={generating}
              variant={invoiceCheckResult?.exists ? "destructive" : "default"}
            >
              {generating ? 'Generating...' : (invoiceCheckResult?.exists ? 'Generate Anyway' : 'Generate Invoices')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Late Fine Confirmation Dialog */}
      <Dialog open={fineConfirmDialog} onOpenChange={setFineConfirmDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-500" />
              Apply Late Fines
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {fineCheckResult?.fines_applied_today > 0 ? (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="font-semibold text-amber-800">⚠️ Warning: Fines Already Applied Today!</p>
                <p className="text-sm text-amber-700 mt-2">
                  Late fines have already been applied to <strong>{fineCheckResult.fines_applied_today}</strong> invoices today.
                </p>
                <p className="text-sm text-amber-700 mt-2">
                  This was likely done by the Daily Cron Job. Applying again may result in double fines!
                </p>
              </div>
            ) : (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="font-semibold text-blue-800">ℹ️ Late Fine Status</p>
                <p className="text-sm text-blue-700 mt-2">
                  <strong>{fineCheckResult?.overdue_invoices || 0}</strong> overdue invoices found.
                </p>
                <p className="text-sm text-blue-700">
                  No fines have been applied today.
                </p>
              </div>
            )}
            
            <p className="text-sm text-muted-foreground">
              This action will calculate and apply late fines to all overdue invoices.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFineConfirmDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={applyLateFines} 
              disabled={applyingFines}
              variant={fineCheckResult?.fines_applied_today > 0 ? "destructive" : "default"}
            >
              {applyingFines ? 'Applying...' : (fineCheckResult?.fines_applied_today > 0 ? 'Apply Anyway' : 'Apply Late Fines')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Assign Dialog */}
      <Dialog open={bulkAssignDialog} onOpenChange={setBulkAssignDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Bulk Assign One-Time Fee</DialogTitle>
            <DialogDescription>
              Assign a one-time fee to all students in a class
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Class</Label>
              <Select 
                value={bulkAssignForm.class_id} 
                onValueChange={(value) => {
                  setBulkAssignForm(prev => ({ ...prev, class_id: value }));
                  fetchStudentsForBulkAssign(value);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select class" />
                </SelectTrigger>
                <SelectContent>
                  {classes.map(cls => (
                    <SelectItem key={cls.id} value={cls.id.toString()}>
                      {cls.name} {cls.section || ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Fee Head</Label>
              <Select 
                value={bulkAssignForm.fee_head_id} 
                onValueChange={(value) => setBulkAssignForm(prev => ({ ...prev, fee_head_id: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select fee head" />
                </SelectTrigger>
                <SelectContent>
                  {feeHeads.filter(head => head.fee_category === 'one_time').map(head => (
                    <SelectItem key={head.id} value={head.id.toString()}>
                      {head.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Amount (₹)</Label>
              <Input
                type="number"
                placeholder="Enter amount"
                value={bulkAssignForm.amount}
                onChange={(e) => setBulkAssignForm(prev => ({ ...prev, amount: e.target.value }))}
              />
            </div>

            {bulkAssignStudents.length > 0 && (
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-sm font-medium">
                  This will assign the fee to {bulkAssignStudents.length} students in this class
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Total amount: ₹{(parseFloat(bulkAssignForm.amount || 0) * bulkAssignStudents.length).toLocaleString()}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkAssignDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleBulkAssign} disabled={bulkAssigning}>
              {bulkAssigning ? 'Assigning...' : 'Assign Fee'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Fee Dialog */}
      <Dialog open={quickFeeDialog} onOpenChange={setQuickFeeDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-yellow-500" />
              Quick Fee
            </DialogTitle>
            <p className="text-sm text-muted-foreground">
              Create and assign a fee to an entire class instantly. Perfect for occasional fees like Puja, Picnic, Event, etc.
            </p>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Fee Name *</Label>
              <Input 
                value={quickFeeForm.name} 
                onChange={e => setQuickFeeForm(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g., Saraswati Puja Fee"
                data-testid="quick-fee-name"
              />
            </div>
            <div>
              <Label>Amount (₹) *</Label>
              <Input 
                type="number"
                value={quickFeeForm.amount} 
                onChange={e => setQuickFeeForm(p => ({ ...p, amount: e.target.value }))}
                placeholder="e.g., 200"
                data-testid="quick-fee-amount"
              />
            </div>
            <div>
              <Label>Select Class *</Label>
              <Select 
                value={quickFeeForm.class_id} 
                onValueChange={v => {
                  setQuickFeeForm(p => ({ ...p, class_id: v }));
                  fetchQuickFeeStudents(v);
                }}
              >
                <SelectTrigger data-testid="quick-fee-class">
                  <SelectValue placeholder="Select class" />
                </SelectTrigger>
                <SelectContent>
                  {classes.map(cls => (
                    <SelectItem key={cls.id} value={cls.id}>{cls.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Due Date (Optional)</Label>
              <Input 
                type="date"
                value={quickFeeForm.due_date} 
                onChange={e => setQuickFeeForm(p => ({ ...p, due_date: e.target.value }))}
                data-testid="quick-fee-due-date"
              />
              <p className="text-xs text-muted-foreground mt-1">Default: 15 days from today</p>
            </div>
            
            {quickFeeStudents.length > 0 && (
              <div className="p-3 bg-green-50 rounded-lg">
                <p className="text-sm font-medium text-green-800">
                  ✓ {quickFeeStudents.length} students will be assigned this fee
                </p>
                {quickFeeForm.amount && (
                  <p className="text-xs text-green-600 mt-1">
                    Total: ₹{(parseFloat(quickFeeForm.amount) * quickFeeStudents.length).toLocaleString()}
                  </p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setQuickFeeDialog(false);
              setQuickFeeForm({ name: '', amount: '', class_id: '', due_date: '' });
              setQuickFeeStudents([]);
            }}>Cancel</Button>
            <Button onClick={handleQuickFee} disabled={quickFeeAssigning} data-testid="quick-fee-submit">
              {quickFeeAssigning ? 'Creating...' : 'Create & Assign'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Payment Confirmation Dialog */}
      <Dialog open={deletePaymentDialog} onOpenChange={setDeletePaymentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="h-5 w-5" />
              Delete Payment
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. All related accounting entries will be deleted.
            </DialogDescription>
          </DialogHeader>
          
          {deletePaymentPreview?.payment && (
            <div className="space-y-4">
              <div className="bg-muted p-4 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Receipt No:</span>
                  <span className="font-medium">{deletePaymentPreview.payment.receipt_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Student:</span>
                  <span className="font-medium">{deletePaymentPreview.payment.student_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Amount:</span>
                  <span className="font-medium text-green-600">₹{deletePaymentPreview.payment.amount?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Date:</span>
                  <span>{deletePaymentPreview.payment.payment_date}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Mode:</span>
                  <span className="uppercase">{deletePaymentPreview.payment.payment_mode}</span>
                </div>
              </div>
              
              {deletePaymentPreview.related_entries && (
                <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg">
                  <p className="text-sm text-amber-800 font-medium mb-2">Related entries to be deleted:</p>
                  <ul className="text-sm text-amber-700 space-y-1">
                    <li>• Journal Entries: {deletePaymentPreview.related_entries.journal_entries}</li>
                    <li>• Cash Entries: {deletePaymentPreview.related_entries.cash_entries}</li>
                    <li>• Bank Entries: {deletePaymentPreview.related_entries.bank_entries}</li>
                  </ul>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeletePaymentDialog(false)} disabled={deletingPayment}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmDeletePayment}
              disabled={deletingPayment}
              data-testid="confirm-delete-payment"
            >
              {deletingPayment ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Payment
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

// Main Export Component - Role-based routing
const FeeManagementPage = () => {
  const { getAuthHeader, user } = useAuth();
  
  // Student/Parent gets simplified view
  if (['student', 'parent'].includes(user?.role)) {
    return <StudentFeeView getAuthHeader={getAuthHeader} user={user} />;
  }
  
  // Admin/School Admin gets full management view
  return <AdminFeeManagement />;
};

export default FeeManagementPage;
